import React, {
	useRef,
} from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import DatePicker from 'react-datepicker';
import {
	useDrop, useDrag,
} from 'react-dnd';
import './fonts.css';

import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-css';
import 'ace-builds/src-noconflict/theme-monokai';
// Bundle the modules ACE would otherwise lazy-load by URL (snippets on mount, searchbox on
// Ctrl+F); the server's SPA fallback answers with index.html ("Unexpected token '<'")
import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/snippets/css';
import 'ace-builds/src-noconflict/ext-searchbox';
import ace from 'ace-builds/src-noconflict/ace';

// Disable web workers to prevent worker-css.js loading errors
ace.config.set('useWorker', false);

// Color for grouped component indicator bar
const GROUPED_COMPONENT_COLOR = '#6366f1';

import kali from 'kali';
import axios from 'axios';
import {
	toCapitalCase, toSnakeCase, getComponentDisplayName,
} from '../../../../../Crow/Common/Crow';
import {
	v4 as uuidv4,
} from 'uuid';
import md5 from 'md5';
import JSONEditor from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import Select from 'react-select';
import {
	selectComponents,
	multiSelectSummaryProps,
	toSlug,
} from './utils/reactSelectUtils';
import {
	isAllowedIconUploadFile,
} from './utils/iconUpload';
import {
	resolvePendingVCLID,
} from './utils/vclUtils';
import {
	humanizeSettingName,
} from './utils/settingNames';
import {
	arrayNames,
	jsonTypes,
	exclusions,
	monarchExclusions,
	pendingDeprecated,
	componentTypeExclusions,
	dropdownSettings,
	dropdownValues,
	listingGroupDropdownOptions,
	listingGroupDropdownValues,
	unusedSettingsSpecific,
	unusedSettingsGeneric,
	readOnlySettingsSpecific,
	numberRanges,
	multipleSelectionValues,
	readOnlySystemGroupSettingsSpecific,
	dropdownStyles,
	multiSummaryDropdownStyles,
} from './systemRules.js';

import {
	Clipboard,
} from './Clipboard.js';

import ListingCollectionWarning from './ListingCollectionWarning';
import ListingColumnInput from './ListingColumnInput';
import ListingColumnCheckbox from './ListingColumnCheckbox';

import style from './TemplateSkeleton.module.css';
import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
	previewHost,
	vultureENV,
} from 'Vulture/ENV';

import {
	getFDValue,
	matchFDKeys,
	parseEntityName,
} from 'Vulture/FormDataHelpers.js';

import {
	downloadCSS,
	uploadNavButtonIcon,
} from 'Vulture/Template';
import {
	buildTestData,
} from 'Vulture/TestData';

import EntertainmentCategoryPicker from './EntertainmentCategoryPicker';
import VisualContentLoopPicker from './VisualContentLoopPicker';

import {
	s3,
} from 'S3';

import {
	mobileClickDirectPDF,
	mobileClickEventOptions,
	touchActionDetailsPageA,
	touchActionDetailsPageB,
	touchActionDirectWayfinding,
	touchActionIframe,
	touchActionNone,
	touchActionPDF,
	touchActionWayfindingA,
	touchActionWayfindingB,
} from './consts';

import API from 'Vulture/lib/API';

import {
	ListingConfigContext,
} from './ListingConfigContext.js';

// Categories for listing_group static settings
const LISTING_GROUP_SETTING_CATEGORIES = {
	'Font Sizing': {
		icon:     'text-height',
		settings: [
			'font_size',
			'font_size_min',
			'fixed_font_size',
			'header_font_size',
			'indented_scaling',
			'indented_scaling_size',
			'bias_font_scale_vs_margin',
		],
	},
	'Spacing & Layout': {
		icon:     'arrows-alt',
		settings: [
			'listing_margin_top_em',
			'listing_margin_bottom_em',
			'fixed_margin_top',
			'fixed_margin_bottom',
			'header_height',
			'header_padding',
			'side_gap_width_left',
			'side_gap_width_right',
			'child_icon_indent',
			'letter_spacing',
			'line_height',
			'listing_grid_fr',
		],
	},
	'Structure & Hierarchy': {
		icon:     'sitemap',
		settings: [
			'num_columns',
			'num_columns_auto',
			'num_columns_max',
			'combined',
			'row_concatenation',
			'icon_parent',
			'icon_child',
			'parent_icon_indent',
			'condor_configuration',
		],
	},
	'Visual Styling': {
		icon:     'paint-brush',
		settings: [
			'show_column_headers',
			'header_first_only',
			'style_parent_header_color',
			'show_listing_lines',
			'show_listing_lines_num',
			'show_listing_bg_color',
			'show_listing_border',
			'show_listing_border_color',
			'vert_line_width',
			'vert_line_color',
			'vert_line_style',
			'vertical_line_full',
			'show_dev_styles',
			'custom_classes',
			'modifier_classes',
		],
	},
	'Customer Portal': {
		icon:     'user-circle',
		settings: [
			'custom_content_sidebar_label',
			'custom_portal_label',
			'is_locked',
		],
	},
	'Order': {
		icon:     'sort-numeric-down',
		settings: [
			'layout_order',
			'tlc_layout_order',
		],
	},
};

// Categories for common (non-listing-group) component settings
const COMMON_SETTING_CATEGORIES = {
	'Customer Portal': {
		icon:     'user-circle',
		settings: [
			'custom_content_sidebar_label',
			'custom_portal_label',
			'is_locked',
		],
	},
	'Styling': {
		icon:     'paint-brush',
		settings: [
			'custom_classes',
			'custom_classes_1',
			'custom_classes_2',
			'custom_classes_3',
			'modifier_classes',
			'modifier_classes_1',
			'modifier_classes_2',
			'modifier_classes_3',
			'line_custom_classes',
			'line_modifier_classes',
			'box_custom_classes',
			'container_classes',
			'container_custom_classes',
			'content_classes',
			'content_custom_classes',
			'condor_layout_classes',
			'custom_inline_css',
			'custom_css',
			'show_dev_styles',
			'font_header',
			'font_body',
			'theme_color_primary',
			'theme_color_secondary',
			'theme_color_tertiary',
			'theme_font_color_primary',
			'theme_font_color_secondary',
			'theme_button_color',
			'theme_iconFilter_color',
		],
	},
	'Order': {
		icon:     'sort-numeric-down',
		settings: [
			'layout_order',
			'tlc_layout_order',
		],
	},
};

// mock_nova_enabled/mock_nova_scenario are fake (non-schema) settings on the
// real 'nova' provider — see getMockNovaEnabledForAccount(). Broken out into
// their own top-of-panel section so they're not lost among the real nova settings.
const MOCK_NOVA_SETTING_CATEGORY = {
	'Mock Nova': {
		icon:     'flask',
		settings: [ 'mock_nova_enabled', 'mock_nova_scenario', 'mock_nova_duration_seconds', ],
	},
};

// Settings rendered at the top, outside categories (no header)
const PRIORITY_SETTINGS = [ 'venue_id', 'is_visual_content_loop', ];

// Settings rendered at the bottom, outside categories, with wide class
const BOTTOM_SETTINGS = [ 'show_on_phase', ];

// Settings that need full width (span both columns) - for JSON editors, textareas, large inputs
const WIDE_SETTINGS = [
	'custom_inline_css',
	'custom_css',
	'custom_classes',
	'modifier_classes',
	'raven_days',
	'categories',
	'amenity_categories',
	'entertainment_categories',
	'show_on_phase',
	'direct_to_listing_uuid',
];

// Helper to check if a setting should be wide
const checkIsWideSetting = (settingData) => {
	return WIDE_SETTINGS.includes(settingData.name) || settingData.type === 'json';
};

// Sort function: wide settings sink to the bottom, otherwise alphabetical by display name.
const sortWideSettingsToBottom = ([ , aVal, ], [ , bVal, ]) => {
	const aIsWide = checkIsWideSetting(aVal);
	const bIsWide = checkIsWideSetting(bVal);
	if (aIsWide && !bIsWide) return 1;
	if (!aIsWide && bIsWide) return -1;
	const aLabel = (aVal.display_name || aVal.name || '').toLowerCase();
	const bLabel = (bVal.display_name || bVal.name || '').toLowerCase();
	return aLabel.localeCompare(bLabel);
};

class TemplateSkeleton extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);

		let expandedOrCollapsedEntities = JSON.parse(sessionStorage.expandedOrCollapsedEntities || '{}');

		this.state = {
			activeEntity:                    {},
			activeEntityKey:                 '',
			componentTypeToSettingMap:       {},
			flatData:                        {},
			isCustom:                        false,
			rules:                           {},
			showSettings:                    true,
			bucketName:                      'ts-condor-custom-css',
			confirmModal:                    false,
			confirmModalContent:             {},
			componentTypeFilter:             '',
			clipboard:                       false,
			customCSSOptions:                [],
			expandedOrCollapsedEntities,
			broodVisualContentLoopProviders: {},
			broodVisualContentLoopResolutions: {},
			staticListingSettingsExpanded:   false,
		};
	}

	componentDidMount() {
		this._isMounted = true;

		this.fetchComponentTypeToSettingMap();
		this.getCustomCSSFiles();

		let clipboard = JSON.parse(sessionStorage.template_skeleton_clipboard || '{}');
		if (Array.isArray(clipboard.entries) && clipboard.entries.length > 0) {
			this.setState({
				clipboard: true,
			});
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (JSON.stringify(prevState.flatData) !== JSON.stringify(this.state.flatData)) {
			this.props.updateSkeletonData(this.state.flatData);
		}

		// Refresh flatData when SystemProgramming pushes new existingFlatData.
		if (!this.areObjectsEqual(prevProps.existingFlatData, this.props.existingFlatData)) {
			// existingFlatData can be null (e.g. TemplateSkeletonBuilder/TemplateBuilder
			// pass null when no skeleton exists); coerce so findIsCustom and the
			// newFlatData lookup below don't throw on null.
			const existingFlatData = this.props.existingFlatData || {};
			let isCustom = this.findIsCustom(existingFlatData);
			this.getCustomCSSFiles();

			const newFlatData = JSON.parse(JSON.stringify(existingFlatData));

			// Preserve the user's active entity across a *soft* refresh — e.g. the
			// post-save refetch — so their open editor (Custom Inline CSS, etc.)
			// doesn't blank out. Only clear the selection when that entity no longer
			// exists in the new data (a real system switch / structural change). (DEV-11883)
			const keepActive = this.state.activeEntityKey && newFlatData[this.state.activeEntityKey];

			this.setState({
				activeEntity:    keepActive ? newFlatData[this.state.activeEntityKey] : {},
				activeEntityKey: keepActive ? this.state.activeEntityKey : '',
				flatData:        newFlatData,
				isCustom,
			});
		}

		// Pass the activeEntityKey back up to SystemProgramming
		if (prevState.activeEntityKey !== this.state.activeEntityKey && this.props.updateActiveEntityKey) {
			this.props.updateActiveEntityKey('activeEntityKey', this.state.activeEntityKey);
		}

		if (sessionStorage.expandedOrCollapsedEntities !== JSON.stringify(this.state.expandedOrCollapsedEntities)) {
			sessionStorage.expandedOrCollapsedEntities = JSON.stringify(this.state.expandedOrCollapsedEntities);
		}
	}

	getCustomCSSFiles() {
		const prefix = `${vultureENV}`;

		const {
			accountUUID,
		} = this.props;

		s3.listObjects(
			{
				Bucket:    this.state.bucketName,
				Delimiter: '/',
				Prefix:    `${prefix}/${accountUUID}/`,
			},
			(err, data) => {
				if (!this._isMounted) {
					return;
				}

				if (err) {
					console.error(err);
					return;
				}

				if (data && data.Contents) {
					if (data.Contents.length === 0) {
						this.setState({
							customCSSOptions: [],
						});

						return;
					}

					const customCSSOptions = data.Contents.map((fileData) => {
						return {
							label: fileData.Key.split('/')[2],
							value: fileData.Key.split('/')[2],
						};
					});

					this.setState({
						customCSSOptions,
					});
				}
			}
		);
	}

	// Main function to recursively compare two JSON-like objects
	areObjectsEqual(obj1, obj2) {
		// Check if both objects are empty or null, in which case they are considered equal
		if (this.isEmptyOrNull(obj1) && this.isEmptyOrNull(obj2)) {
			return true;
		}

		// Check for null in either of the objects, in which case they are considered not equal
		if (obj1 === null || obj2 === null) {
			return false;
		}

		// Check if both objects are of the same type
		if (typeof obj1 !== typeof obj2) {
			return false;
		}

		// Short-cirtuit: check if the number of properties is different
		if (Object.keys(obj1).length !== Object.keys(obj2).length) {
			return false; // Different number of properties, so not equal
		}

		// Recursively compare all properties of obj1 with obj2
		for (const key in obj1) {
			if (!(key in obj2)) {
				return false; // Property in obj1 is missing in obj2
			}
			if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
				// Both properties are objects, so we need to recursively compare them
				if (!this.areObjectsEqual(obj1[key], obj2[key])) {
					return false; // Recursive comparison found a difference
				}
			} else if (obj1[key] !== obj2[key]) {
				return false; // Direct comparison found a difference
			}
		}

		// Check for extra properties in obj2 that are not in obj1
		for (const key in obj2) {
			if (!(key in obj1)) {
				return false; // Property in obj2 is missing in obj1
			}
		}

		return true; // No differences found, objects are equal
	}

	// Helper function to check if an object is either empty or has all properties set to null
	isEmptyOrNull(obj) {
		if (obj === null) {
			return true; // The object is null
		}
		for (const key in obj) {
			if (obj[key] !== null) {
				return false; // Found a property that is not null
			}
		}
		return true; // No properties or all properties are null
	}

	stateCallback(key, val) {
		if (typeof key !== undefined && typeof val !== undefined) {
			this.setState({
				[key]: val,
			});
		}
	}

	handleDuplicateName(file) {
		let systemOrAccountName = (this.props.activeSystemName ? this.props.activeSystemName + ' for this account' : 'this account');
		this.setState({
			confirmModal:        true,
			confirmModalContent:
				<>
					<p>{file.name + ' already exists on ' + systemOrAccountName + '. Please rename the .css file to a unique name and re-upload.'}</p>
					<div className='confirmModalButtonContainer'>
						<div className='confirm-modal-buttons'>
							<button className='button' onClick={() => {
								this.setState({
									confirmModal: false,
								});
							}}>Okay</button>
						</div>
					</div>
				</>,
		});
	}

	uploadCSS(file, settingKey, type) {
		const {
			app,
			accountUUID,
		} = this.props;

		app.setState({
			loading: true,
		});

		let objKey = `${vultureENV}/${accountUUID}/${file.name}`;
		let params = {
			Bucket: this.state.bucketName,
			Key:    objKey,
		};
		// Using callbacks

		s3.headObject(params, function (err) {
			console.error('err', err);
			if (err && err.name === 'NotFound') {
				// Handle no object on cloud here
				s3.upload(
					{
						Body:        file,
						Bucket:      this.state.bucketName,
						Key:         objKey,
						ACL:         'public-read',
						ContentType: 'text/css',
					},
					(err, data) => {
						if (!this._isMounted) {
							try {
								app.setState({
									loading: false,
								});
							} catch (e) {}
							return;
						}

						if (err) {
							console.error(err, data);
							alert(`There was an error uploading your file: ${err.message}`);
							app.setState({
								loading: false,
							});
							return;
						}
						app.setState({
							loading: false,
						});

						this.updateSettingValue(settingKey, objKey, type, false);
					}
				);
			} else if (err) {
				// Handle other errors here....
			} else {
				s3.getSignedUrl('getObject', params, this.handleDuplicateName(file));
				app.setState({
					loading: false,
				});
			}
		}.bind(this));
	}

	handleCustomCSSUpload(e, settingKey, type) {
		e.preventDefault();
		if (e.target && e.target.files) {
			const file = e.target.files[0];
			this.uploadCSS(file, settingKey, type);
		}
	}

	deleteCSS(settingKey, type) {
		this.updateSettingValue(settingKey, '', type, false);
	}

	uploadIcon(file, activeEntityKey) {
		const { app, accountUUID, settingMap } = this.props;
		if (!file) return;

		const iconUploadKey = settingMap?.icon_upload?.uuid ? `setting.${settingMap.icon_upload.uuid}` : null;
		const iconUploadNameKey = settingMap?.icon_upload_name?.uuid ? `setting.${settingMap.icon_upload_name.uuid}` : null;
		if (!iconUploadKey || !iconUploadNameKey) {
			console.error('icon_upload settings missing from settingMap');
			this.setState({
				confirmModal: true,
				confirmModalContent: (
					<>
						<p>{'Image upload is not available: the icon_upload settings are missing from this environment.'}</p>
						<div className='confirmModalButtonContainer'>
							<div className='confirm-modal-buttons'>
								<button type="button" className="button" onClick={() => this.setState({ confirmModal: false })}>Okay</button>
							</div>
						</div>
					</>
				),
			});
			return;
		}

		app.setState({ loading: true });

		// The file bytes are uploaded via a bronco proxy (S3 write happens server-side);
		// assigning the returned key still flows through the normal flatData path, so the
		// save cycle and preview behavior are unchanged.
		uploadNavButtonIcon(file, accountUUID).then((key) => {
			if (!this._isMounted) {
				try { app.setState({ loading: false }); } catch (e) {}
				return;
			}

			// Avoid pass by reference mutation!
			let flatData = JSON.parse(JSON.stringify(this.state.flatData || {}));
			// flatData may have been replaced while the upload was in flight
			// (system/template switch) — don't fabricate a stub entity.
			if (!flatData[activeEntityKey]) {
				app.setState({ loading: false });
				return;
			}
			// Entities created before these settings existed have no entry yet.
			flatData[activeEntityKey][iconUploadKey] = {
				...flatData[activeEntityKey][iconUploadKey],
				string: key,
			};
			flatData[activeEntityKey][iconUploadNameKey] = {
				...flatData[activeEntityKey][iconUploadNameKey],
				string: file.name,
			};
			this.setState({ flatData }, () => {
				app.setState({ loading: false });
			});
		}).catch((err) => {
			// Clear the app-level loading flag even if we unmounted mid-upload,
			// otherwise a failed upload during a system/template switch leaves the
			// spinner stuck.
			app.setState({ loading: false });
			if (!this._isMounted) { return; }
			console.error(err);
			this.setState({
				confirmModal: true,
				confirmModalContent: (
					<>
						<p>{`There was an error uploading your file: ${err && err.message ? err.message : 'unknown error'}`}</p>
						<div className='confirmModalButtonContainer'>
							<div className='confirm-modal-buttons'>
								<button type="button" className="button" onClick={() => this.setState({ confirmModal: false })}>Okay</button>
							</div>
						</div>
					</>
				),
			});
		});
	}

	handleIconUpload(e, activeEntityKey) {
		e.preventDefault();
		if (!(e.target && e.target.files && e.target.files.length > 0)) return;
		const file = e.target.files[0];
		if (!isAllowedIconUploadFile(file)) {
			this.setState({
				confirmModal: true,
				confirmModalContent: (
					<>
						<p>{'Only PNG and SVG files are allowed for Upload Image.'}</p>
						<div className='confirmModalButtonContainer'>
							<div className='confirm-modal-buttons'>
								<button type="button" className="button" onClick={() => this.setState({ confirmModal: false })}>Okay</button>
							</div>
						</div>
					</>
				),
			});
			return;
		}

		this.uploadIcon(file, activeEntityKey);
	}

	// Unassigns the uploaded icon from the button by clearing the icon_upload /
	// icon_upload_name setting values. This only removes the assignment (a normal
	// pending change that still needs Save); the S3 asset is intentionally left in
	// place — this is not an asset delete.
	deleteIcon(activeEntityKey) {
		const { settingMap } = this.props;
		const iconUploadKey = settingMap?.icon_upload?.uuid ? `setting.${settingMap.icon_upload.uuid}` : null;
		const iconUploadNameKey = settingMap?.icon_upload_name?.uuid ? `setting.${settingMap.icon_upload_name.uuid}` : null;
		if (!iconUploadKey || !iconUploadNameKey) return;

		// Avoid pass by reference mutation!
		let flatData = JSON.parse(JSON.stringify(this.state.flatData || {}));
		if (!flatData[activeEntityKey]) return;

		flatData[activeEntityKey][iconUploadKey] = {
			...flatData[activeEntityKey][iconUploadKey],
			string: '',
		};
		flatData[activeEntityKey][iconUploadNameKey] = {
			...flatData[activeEntityKey][iconUploadNameKey],
			string: '',
		};
		this.setState({ flatData });
	}

	fetchComponentTypeToSettingMap() {
		const url = `${broncoURL}/template_skeleton`;
		const sessionKey = getSession().session;

		new kali({
			headers: {
				'content_type':   'application/json',
				'X-Auth-Session': sessionKey,
			},
			method: 'POST',
		}).post(url, {
			success: (_kali, res, contents) => {
				if (!this._isMounted) {
					return;
				}

				if (contents) {
					let flatData = {};
					let componentTypeListForRules = [];
					// Using invalid UUIDs so /w/edit will create new UUIDs each time a TLC is generated from a skeleton.
					const uuid = `_${uuidv4()}`;
					const tlcKey = `component.${uuid}`;

					// Create TLC in flatData.
					Object.entries(contents).forEach(([ componentTypeKey, componentTypeVal, ]) => {
						if (componentTypeVal.type === 'top_level_condor') {
							const flatObj = this.setDefaultValues(componentTypeKey, componentTypeVal);
							flatData[tlcKey] = flatObj;

							return;
						}

						// List of component_types that cannot be owned by a TLC directly.
						const componentTypeIgnoreList = [
							'listing_group',
							'monarch',
							'nova',
							'peacock',
							'pelican',
							'broadsign_feed',
							'entertainment',
						];

						if (!~componentTypeIgnoreList.indexOf(componentTypeVal.type)) {
							componentTypeListForRules.push(componentTypeVal.type);
						}
					});

					this.initRules(componentTypeListForRules);

					// Sort component_types by type and reduce into new object
					const sortedData = Object.entries(contents)
						.sort(([ aKey, aVal, ], [ bKey, bVal, ]) => {
							if (aVal.type > bVal.type) {
								return 1;
							}
							if (aVal.type < bVal.type) {
								return -1;
							}
							return 0;
						}).reduce((obj, [ key, ]) => {
							obj[key] = contents[key];
							return obj;
						}, {});

					let isCustom = false;

					if (this.props.existingFlatData) {
						flatData = this.props.existingFlatData;
						isCustom = this.findIsCustom(flatData);
					}

					this.setState({
						// If this is a new skeleton, make the TLC active.
						...!this.props.existingFlatData && {
							activeEntity:    flatData[tlcKey],
							activeEntityKey: tlcKey,
						},
						componentTypeToSettingMap: sortedData,
						flatData,
						isCustom,
					});
				}
			},

			failure: (_kali, err) => {
				console.error(err);
			},
		});
	}

	fetchBroodVisualContentLoopProviders(broodVisualContentLoopID, cb = () => {}) {
		if (!broodVisualContentLoopID) {
			if (typeof cb === 'function') {
				return cb();
			}
		}

		API.findRecord('visual_content_loop', broodVisualContentLoopID, {})
			.then((response) => {
				if (!this._isMounted) {
					if (typeof cb === 'function') {
						return cb();
					}
					return;
				}

				const now = (new Date()).getTime();

				this.setState({
					broodVisualContentLoopProviders: {
						...this.state.broodVisualContentLoopProviders,
						[broodVisualContentLoopID]: response.providers.map((provider) => {
							return {
								...provider,
								is_visual_content_loop_provider: true,
								lastFetchedTime:                 now,
							};
						}),
					},
					// VCL-level resolution override (DEV-11111); empty = inherit account default
					broodVisualContentLoopResolutions: {
						...this.state.broodVisualContentLoopResolutions,
						[broodVisualContentLoopID]: response.resolution || '',
					},
				}, () => {
					if (typeof cb === 'function') {
						return cb();
					}
				});
			}).catch((err) => {
				console.error(err);
			});
	}

	initRules(componentTypeList) {
		const filteredComponentTypeList = componentTypeList.filter((componentType) => {
			if (componentType.startsWith('monarch_feed_')) {
				return false;
			}

			if (componentType === 'presence_detection') {
				return false;
			}

			if (componentType === 'info_box') {
				return false;
			}

			if (componentType === 'info_box_header') {
				return false;
			}

			if (componentType === 'info_box_header_block') {
				return false;
			}

			if (componentType === 'info_box_img') {
				return false;
			}

			if (componentType === 'info_box_text') {
				return false;
			}

			return true;
		});

		const rules = {
			'animated_scroller':  [ 'clock', 'date_display', 'dynamic_message', 'image_component', 'info_box_group', 'layout_group', 'news', 'qr_code', 'stocks', 'text_box', 'text_list', 'weather', ],
			'brood':              [ 'monarch', 'nova', 'peacock', 'pelican', 'broadsign_feed', 'entertainment', ],
			'monarch':            [],
			'info_box_group':     [ 'info_box', ],
			'info_box':           [ 'info_box_header', 'info_box_header_block', 'info_box_img', 'info_box_text', ],
			'layout_group':       filteredComponentTypeList,
			'listings_scroller':  [ 'listing_collection', ],
			'listing_collection': [ 'listing_group', ],
			'top_level_condor':   [ ...filteredComponentTypeList, 'presence_detection', ],
			'navigation':         [ 'navigation_button', ],
			'navigation_button':  [ 'PDF Component', ],
			'mobile':             [ 'mobile_url', ],
		};

		rules['monarch'] = componentTypeList.filter((componentType) => {
			return componentType.startsWith('monarch_feed_') && !monarchExclusions.includes(componentType);
		});

		this.setState({
			rules,
		});
	}

	findIsCustom(flatData = {}) {
		const {
			customConfig,
		} = this.props;

		if (customConfig) {
			return true;
		}

		let isCustomUUID = '';

		if (this.props.settingMap && this.props.settingMap['is_custom']) {
			isCustomUUID = this.props.settingMap['is_custom'].uuid;
		}

		for (const data of Object.values(flatData)) {
			if (data.type === 'top_level_condor' && data[`setting.${isCustomUUID}`] && data[`setting.${isCustomUUID}`].bool) {
				return data[`setting.${isCustomUUID}`].bool;
			}
		}

		return false;
	}

	setDefaultValues(componentTypeEntityKey, componentTypeData) {
		let flatObj = {};
		let type = '';

		Object.entries(componentTypeData).forEach(([ componentTypeChildKey, componentTypeChildVal, ]) => {
			if (componentTypeChildKey.startsWith('setting.')) {
				let defaultVal = componentTypeChildVal.default_value;
				const {
					type: attr, name, required,
				} = componentTypeChildVal;

				if (name === 'condor_render_name' || name === 'custom_content_sidebar_label') {
					return;
				}

				// If the setting is required and there's no default value in the DB, set a zero value.
				if (required && defaultVal === null) {
					switch (attr) {
					case 'bool':
						defaultVal = false;
						break;
					case 'date':
					case 'datetime':
					case 'timestamp':
						defaultVal = '2006-01-02T15:04:05Z';
						break;
					case 'file':
					case 'string':
					case 'text':
						defaultVal = '';
						break;
					case 'float':
					case 'int':
						defaultVal = 0;
						break;
					case 'json':
						defaultVal = {};
						break;
					case 'time':
						defaultVal = '00:00:00';
						break;
					default:
						break;
					}
				}

				const [ m2, m2ID, ] = componentTypeChildKey.split('.');

				flatObj = {
					...flatObj,
					[`${m2}.${m2ID}`]: {
						[attr]: defaultVal,
					},
				};
			}

			if (componentTypeChildKey === 'type') {
				type = componentTypeChildVal;
			}
		});

		const friendlyType = getComponentDisplayName(type);
		const renderName = toCapitalCase(type).replace(/\s/g, '');

		// Set the condor_render_name default setting
		if (type !== 'top_level_condor') {
			flatObj = {
				...flatObj,
				condor_render_name: renderName,
			};
		}

		flatObj = {
			...flatObj,
			[componentTypeEntityKey]: {},
			'display_name':           friendlyType,
			'description':            type,
			type,
		};

		return flatObj;
	}

	addEntity(componentTypeKey) {
		const {
			activeEntityKey,
			componentTypeToSettingMap,
			flatData,
			expandedOrCollapsedEntities,
		} = this.state;

		const {
			activeTLCUUID,
			settingMap,
			fd,
		} = this.props;

		const {
			activeSystemGroup,
			activeSystemFlatData,
		} = this.props;

		const uuid = `_${uuidv4()}`;

		// Avoid pass by reference mutation!
		let data = JSON.parse(JSON.stringify(flatData));

		data[activeEntityKey] = {
			...data[activeEntityKey],
			[`component.${uuid}`]: {},
		};

		const flatObj = this.setDefaultValues(componentTypeKey, componentTypeToSettingMap[componentTypeKey]);
		data[`component.${uuid}`] = {
			...flatObj,
			parent: activeEntityKey,
		};

		// Create a component_to_listing_collection relation.
		if (componentTypeToSettingMap[componentTypeKey].type === 'listing_collection') {
			const listingCollectionUUID = `_${uuidv4()}`;
			data[`component.${uuid}`][`listing_collection.${listingCollectionUUID}`] = {};
			data[`listing_collection.${listingCollectionUUID}`] = {
				parent: `component.${uuid}`,
			};
		}

		// Create component_to_listing_group relation.
		if (componentTypeToSettingMap[componentTypeKey].type === 'listing_group') {
			const isTouch = !!flatData?.[activeTLCUUID]?.[`setting.${settingMap?.is_touch?.uuid}`]?.bool;
			const listingGroupUUID = `_${uuidv4()}`;
			// Add listing_group entity to flatData.
			data[`component.${uuid}`][`listing_group.${listingGroupUUID}`] = {};
			data[`listing_group.${listingGroupUUID}`] = {
				parent: `component.${uuid}`,
			};

			for (const m1Key of Object.keys(data[activeEntityKey])) {
				if (m1Key.startsWith('listing_collection.') && fd[m1Key]) {
					for (const m2Key of Object.keys(fd[m1Key])) {
						if (m2Key.startsWith('listing_config.')) {
							let isParentConfig = true;
							let currentConfig = m2Key;
							while (isParentConfig) {
								data[`listing_group.${listingGroupUUID}`][currentConfig] = {
									show_sub_nav_button: isTouch,
									mobile_display:      true,
								};
								isParentConfig = false;

								Object.keys(fd[currentConfig]).forEach((key) => {
									if (key.startsWith('listing_config.')) {
										isParentConfig = true;
										currentConfig = key;
									}
								});
							}
						}
					}
				}
			}
		}

		// Create a component_to_listing_collection relation.
		if (componentTypeToSettingMap[componentTypeKey].type === 'entertainment') {
			const listingCollectionUUID = `_${uuidv4()}`;
			data[`component.${uuid}`][`listing_collection.${listingCollectionUUID}`] = {};
			data[`listing_collection.${listingCollectionUUID}`] = {
				parent: `component.${uuid}`,
			};
		}

		// Set an initial custom value for Venue ID.
		if ([ 'peacock', 'nova', ].includes(componentTypeToSettingMap[componentTypeKey].type)) {
			if (activeSystemGroup && activeSystemFlatData && settingMap?.venue_id?.uuid) {
				data[`component.${uuid}`][`setting.${settingMap.venue_id.uuid}`] = {
					string: `${activeSystemGroup.project_number}${activeSystemFlatData.directory_number ? `-${activeSystemFlatData.directory_number}` : ''}`,
				};
			}
		}

		expandedOrCollapsedEntities[activeEntityKey] = true;

		this.setState({
			expandedOrCollapsedEntities,
			flatData: data,
		});
	}

	cloneChildren(orgEntityKey, parentKey, flatData) {
		const {
			m1,
		} = parseEntityName(orgEntityKey);

		let orgEntity = Object.assign({}, flatData[orgEntityKey]);

		// Clone the orignal entity into a new key.
		const newEntityKey = `${m1}._${uuidv4()}`;
		flatData[newEntityKey] = JSON.parse(JSON.stringify(orgEntity));

		// Update the parent key on the entity.
		flatData[newEntityKey].parent = parentKey;

		// Assign cloned entity to the parent.
		flatData[parentKey][newEntityKey] = {};

		const entityCloneList = [
			'component',
			'listing_collection',
			'listing_group',
		];

		// Remove any orignal child components from the cloned entity and replace them with cloned children.
		Object.keys(flatData[newEntityKey]).forEach((childKey) => {
			const {
				m1: childM1,
			} = parseEntityName(childKey);

			if (~entityCloneList.indexOf(childM1)) {
				delete flatData[newEntityKey][childKey];
				this.cloneChildren(childKey, newEntityKey, flatData);
			}
		});

		return flatData;
	}

	cloneEntity() {
		const {
			activeEntity, activeEntityKey, flatData,
		} = this.state;

		// Avoid pass by reference mutation!
		let data = JSON.parse(JSON.stringify(flatData));

		let updatedData = this.cloneChildren(activeEntityKey, activeEntity.parent, data);
		this.setState({
			activeEntity:    {},
			activeEntityKey: '',
			flatData:        updatedData,
		});
	}

	deleteEntityFromFlatData(entityKey, flatData) {
		if (!flatData[entityKey]) {
			return flatData;
		}

		const {
			m1ID,
		} = parseEntityName(entityKey);

		// Remove entity from parent.
		const parentEntityKey = flatData[entityKey].parent;
		if (parentEntityKey) {
			// Delete mock data from flatData.
			if (m1ID.length !== 36) {
				delete flatData[parentEntityKey][entityKey];
			} else {
				// Set real data relations to false.
				if (flatData[parentEntityKey]) {
					flatData[parentEntityKey][entityKey] = false;
				}
			}
		}

		let condorRenderNameM1Key = this.props.settingMap?.['condor_render_name']?.uuid ?
			`setting.${this.props.settingMap['condor_render_name'].uuid}` : null;
		let customContentSidebarLabelM1Key = this.props.settingMap?.['custom_content_sidebar_label']?.uuid ?
			`setting.${this.props.settingMap['custom_content_sidebar_label'].uuid}` : null;

		let settingsToSkip = [
			condorRenderNameM1Key,
			customContentSidebarLabelM1Key,
		];

		// Recursively delete the children.
		Object.keys(flatData[entityKey]).forEach((childKey) => {
			const {
				m1,
				m1ID,
			} = parseEntityName(childKey);

			if (childKey.startsWith('setting.') && settingsToSkip.includes(childKey)) {
				delete flatData[entityKey][childKey];
				return;
			}

			if (m1 && m1ID) {
				// Delete mock data from flatData.
				if (m1ID.length !== 36) {
					delete flatData[entityKey][childKey];
				} else {
					// Set real data relations to false.
					flatData[entityKey][childKey] = false;
				}

				// if component is a pelican one, we should not delete its children (the slides)
				const isPelican = flatData[entityKey].description === 'pelican';

				if (m1 === 'component' && !isPelican || m1.startsWith('listing_')) {
					this.deleteEntityFromFlatData(childKey, flatData);
				}
			}
		});

		if (m1ID.length !== 36) {
			delete flatData[entityKey];
		}

		return flatData;
	}

	deleteEntity() {
		const {
			activeEntity,
			activeEntityKey,
			flatData,
		} = this.state;

		// Avoid pass by reference mutation!
		let data = JSON.parse(JSON.stringify(flatData));

		if (window.confirm(`Are you sure you want to delete ${activeEntity.display_name} and all its children?`)) {
			let updatedData = this.deleteEntityFromFlatData(activeEntityKey, data);
			this.setState({
				activeEntity:    {},
				activeEntityKey: '',
				flatData:        updatedData,
			});
		}
	}

	saveAsTemplate() {
		const templateName = window.prompt('Enter a name for this template:');
		if (!templateName) {
			return;
		}

		const templateSkeletonData = this.convertFlatDataToTemplateSkeleton(this.state.flatData);

		const {
			activeSystemFlatData,
			fd,
		} = this.props;

		let sourceTemplateUUID = '';
		Object.keys(activeSystemFlatData || {}).forEach((key) => {
			if (key.startsWith('template.')) {
				sourceTemplateUUID = key.split('.')[1];
			}
		});

		let existingTemplateCategoryUUID = '';
		let existingTemplateConfigUUID = '';
		let existingTemplateThemeUUID = '';

		if (sourceTemplateUUID && fd && fd[`template.${sourceTemplateUUID}`]) {
			const sourceTemplate = fd[`template.${sourceTemplateUUID}`];

			// Get the relations from the source template
			Object.keys(sourceTemplate).forEach((key) => {
				if (key.startsWith('template_category.')) {
					existingTemplateCategoryUUID = key.split('.')[1];
				} else if (key.startsWith('template_config.')) {
					existingTemplateConfigUUID = key.split('.')[1];
				} else if (key.startsWith('template_theme.')) {
					existingTemplateThemeUUID = key.split('.')[1];
				}
			});
		}

		const templateSkeletonUUID = uuidv4();
		const templateSkeletonHash = md5(JSON.stringify(templateSkeletonData));
		const templateUUID = uuidv4();
		const templateConfigUUID = uuidv4();

		let pendingUpdates = {};

		pendingUpdates[`template_skeleton.${templateSkeletonUUID}.display_name`] = templateName;
		pendingUpdates[`template_skeleton.${templateSkeletonUUID}.data`] = JSON.stringify(templateSkeletonData);
		pendingUpdates[`template_skeleton.${templateSkeletonUUID}.hash`] = templateSkeletonHash;

		pendingUpdates[`template.${templateUUID}.display_name`] = templateName;
		pendingUpdates[`account.${this.props.accountUUID}.template.${templateUUID}`] = true;

		if (existingTemplateConfigUUID) {
			const existingDisplayName = fd[`template_config.${existingTemplateConfigUUID}`]?.display_name || templateName;
			pendingUpdates[`template_config.${templateConfigUUID}.display_name`] = existingDisplayName;

			pendingUpdates[`template_config.${templateConfigUUID}.is_global`] = false;
		} else {
			pendingUpdates[`template_config.${templateConfigUUID}.display_name`] = templateName;
			pendingUpdates[`template_config.${templateConfigUUID}.is_global`] = false;
		}

		pendingUpdates[`template.${templateUUID}.template_config.${templateConfigUUID}`] = true;
		pendingUpdates[`template_config.${templateConfigUUID}.template_skeleton.${templateSkeletonUUID}`] = true;

		if (existingTemplateConfigUUID && fd[`template_config.${existingTemplateConfigUUID}`]) {
			Object.keys(fd[`template_config.${existingTemplateConfigUUID}`]).forEach((key) => {
				if (key.startsWith('template_theme.')) {
					const themeUUID = key.split('.')[1];
					pendingUpdates[`template_config.${templateConfigUUID}.template_theme.${themeUUID}`] = true;
				}
			});
		}

		if (existingTemplateCategoryUUID) {
			pendingUpdates[`template.${templateUUID}.template_category.${existingTemplateCategoryUUID}`] = true;
			pendingUpdates[`template_category.${existingTemplateCategoryUUID}.template_config.${templateConfigUUID}`] = true;
		}

		if (existingTemplateThemeUUID) {
			pendingUpdates[`template.${templateUUID}.template_theme.${existingTemplateThemeUUID}`] = true;
		}

		this.props.stateCallback('pendingUpdates', pendingUpdates);
	}

	convertFlatDataToTemplateSkeleton(existingFlatData) {
		const templateSkeletonData = {};
		const uuidMapping = {}; // Maps old UUIDs to new template UUIDs

		// Helper function to check if an entity is a pelican_slide
		const isPelicanSlideEntity = (entityKey, entityData) => {
			// Check explicit type or component_type
			if (entityData.type === 'pelican_slide' || entityData.component_type === 'pelican_slide') {
				return true;
			}
			// Check if it's a child of pelican with undefined type (the most common case)
			const parentKey = entityData.parent;
			if (parentKey && entityData.type === undefined) {
				const parentEntity = existingFlatData[parentKey];
				return parentEntity && parentEntity.type === 'pelican';
			}
			return false;
		};

		Object.keys(existingFlatData).forEach((entityKey) => {
			const [ , entityUUID, ] = entityKey.split('.');
			if (entityUUID && !uuidMapping[entityUUID]) {
				uuidMapping[entityUUID] = `_${uuidv4()}`;
			}
		});

		Object.entries(existingFlatData).forEach(([ oldEntityKey, entityData, ]) => {
			// Skip pelican_slide components - they should not be saved in templates
			if (isPelicanSlideEntity(oldEntityKey, entityData)) {
				return;
			}

			const [ entityType, oldUUID, ] = oldEntityKey.split('.');
			const newUUID = uuidMapping[oldUUID];
			const newEntityKey = `${entityType}.${newUUID}`;

			let newEntityData = JSON.parse(JSON.stringify(entityData));

			// Update any UUID references within the entity data (relations like "component.uuid123")
			const updatedEntityData = {};
			Object.entries(newEntityData).forEach(([ key, value, ]) => {
				if (key.includes('.')) {
					const [ relationType, relationUUID, ] = key.split('.');

					// Skip references to pelican_slide components
					if (relationType === 'component' && relationUUID) {
						const referencedComponentKey = `component.${relationUUID}`;
						const referencedComponent = existingFlatData[referencedComponentKey];
						if (referencedComponent && isPelicanSlideEntity(referencedComponentKey, referencedComponent)) {
							return;
						}
					}

					// Check if this UUID should be remapped (exists in our mapping)
					if (uuidMapping[relationUUID]) {
						const newRelationKey = `${relationType}.${uuidMapping[relationUUID]}`;
						updatedEntityData[newRelationKey] = value;
					} else {
						// Keep the original relationship if UUID not in mapping
						// This handles references to external entities like listing_column, listing_config
						updatedEntityData[key] = value;
					}
				} else {
					updatedEntityData[key] = value;
				}
			});

			// Handle parent references
			if (updatedEntityData.parent) {
				const [ parentType, parentUUID, ] = updatedEntityData.parent.split('.');
				if (uuidMapping[parentUUID]) {
					updatedEntityData.parent = `${parentType}.${uuidMapping[parentUUID]}`;
				}
			}

			templateSkeletonData[newEntityKey] = updatedEntityData;
		});

		return templateSkeletonData;
	}

	renderComponentTypes() {
		const {
			activeEntity, componentTypeToSettingMap, componentTypeFilter, rules,
		} = this.state;

		const acceptedComponentTypeList = rules?.[activeEntity?.type] || [];

		return (
			<div className={style.dataContainer}>
				<div className={style.skeletonContainerHeader}>
					<div className={style.headerLabel}>ADD COMPONENT</div>
					<input
						type='text'
						placeholder='filter'
						onChange={(e) => {
							this.setState({
								componentTypeFilter: e.target.value,
							});
						}}
					/>
				</div>
				<div className={style.scrollingWrapper}>
					{componentTypeToSettingMap && Object.entries(componentTypeToSettingMap)
						.filter(([ componentTypeKey, componentTypeVal, ]) => {
							if (componentTypeFilter === '') {
								return true;
							}

							const searchableType = componentTypeVal.type.startsWith('monarch_feed_')
								? componentTypeVal.type.slice(13)
								: componentTypeVal.type;

							if (~toSnakeCase(searchableType).indexOf(toSnakeCase(componentTypeFilter))) {
								return true;
							}

							if (~getComponentDisplayName(componentTypeVal.type).toLowerCase().indexOf(componentTypeFilter.toLowerCase())) {
								return true;
							}

							return false;
						})
						.map(([ componentTypeKey, componentTypeVal, ], i) => {
							if (componentTypeVal.type && componentTypeVal.type !== 'top_level_condor') {
								if (!~acceptedComponentTypeList.indexOf(componentTypeVal.type)) {
									return '';
								}

								let friendlyType;

								if (componentTypeVal.type.startsWith('monarch_feed_')) {
									friendlyType = toCapitalCase(componentTypeVal.type.slice(13));
								} else {
									friendlyType = getComponentDisplayName(componentTypeVal.type);
								}
								const componentClassName = `${style.componentType} ${
									pendingDeprecated.includes(componentTypeVal.type) ? style.componentTypeDeprecated : ''
								  }`;

								return (
									<div
										data-test-id="componentType"
										className={componentClassName}
										key={i}
										onClick={(e) => {
											e.preventDefault();
											this.addEntity(componentTypeKey);
										}}
									>
										{friendlyType}
										<CustomIcon icon='plus-circle' />
									</div>
								);
							}
						}
						)}
				</div>
			</div>
		);
	}

	async updateSettingValue(settingKey, settingVal, attr, updateBundle = false) {
		const {
			activeEntityKey,
			activeEntity,
			flatData,
		} = this.state;

		const {
			app,
			accountUUID,
			activeSystemUUID,
			settingMap,
		} = this.props;

		const customContentSidebarLabelUUID = settingMap?.['custom_content_sidebar_label']?.uuid;
		if (customContentSidebarLabelUUID && settingKey === `setting.${customContentSidebarLabelUUID}`) {
			settingKey = 'custom_content_sidebar_label';
		}

		// Avoid pass by reference mutation!
		let data = JSON.parse(JSON.stringify(flatData));

		if (activeEntity.is_visual_content_loop_provider) {
			if (!data?.[activeEntityKey]) {
				data[activeEntityKey] = Object.assign({}, activeEntity);
			}
			if (!data[activeEntityKey].settings) {
				data[activeEntityKey].settings = {};
			}
			if (!data[activeEntityKey].settings[activeSystemUUID]) {
				data[activeEntityKey].settings[activeSystemUUID] = {};
			}

			data[activeEntityKey].settings[activeSystemUUID][settingKey] = settingVal;

			// backfill any missing settings since w/e is sent to bronco
			// overrides the entire settings object for that systemUUID
			for (const [ k, v, ] of Object.entries(data[activeEntityKey]?.settings?.[activeSystemUUID] || {})) {
				if (typeof data[activeEntityKey]?.settings?.[activeSystemUUID]?.[k] === 'undefined') {
					data[activeEntityKey].settings[activeSystemUUID][k] = v;
				}
			}

			this.setState({
				flatData: data,
			});

			return;
		}

		if (settingKey !== 'custom_content_sidebar_label') {
			if (!data?.[activeEntityKey]?.[settingKey]) {
				if (attr !== 'filter') {
					app.fetchAccountSystem(accountUUID, activeSystemUUID);
					return;
				}
			}
		}

		if (settingKey.includes('column_group')) {
			let column_group = settingKey.split(',');
			data[column_group[0]][column_group[1]][attr] = settingVal;

			if (updateBundle) {
				data = this.updateBundle(settingVal, data);
			}
			this.setState({
				flatData: data,
			});
			return;
		}

		if (settingKey === 'custom_content_sidebar_label') {
			data[activeEntityKey][settingKey] = settingVal;
		} else {
			data[activeEntityKey][settingKey][attr] = settingVal;
		}

		if (updateBundle && !activeEntity.is_visual_content_loop_provider) {
			data = this.updateBundle(settingVal, data);
		}

		this.setState({
			flatData: data,
		});
	}

	// If the custom_content_sidebar_label has been changed, update the _bundle.component if there is one.
	updateBundle(newVal, data) {
		const {
			activeEntityKey,
		} = this.state;

		const {
			m1, m1ID,
		} = parseEntityName(activeEntityKey);
		if (m1ID.length !== 36) {
			return data;
		}

		const fd = this.props.fd;
		matchFDKeys(fd, `${m1}.${m1ID}._bundle`).forEach((entityKey) => {
			const bundleList = getFDValue(fd, entityKey);
			if (bundleList) {
				let bundleListCopy = JSON.parse(JSON.stringify(bundleList));

				bundleListCopy.forEach((bundle) => {
					if (bundle.attr?.component) {
						bundle.attr.component = newVal;
					}
				});

				data[activeEntityKey]['_bundle'] = bundleListCopy;
			}
		});

		return data;
	}

	renderSystemGroupSettings(settingName) {
		const {
			settingMap, activeSystemGroup, settingsFlatData, activeSystemFlatData,
		} = this.props;

		if (!settingMap || !settingsFlatData || !activeSystemGroup || !activeSystemFlatData) {
			return null;
		}

		if (!settingMap[settingName]) {
			return null;
		}

		const settingUUID = settingMap[settingName].uuid;

		if (!settingsFlatData[`setting.${settingUUID}`]) {
			return null;
		}

		const type = settingMap[settingName].type;
		const setting = activeSystemGroup[`setting.${settingUUID}`];
		const {
			name, description, display_name,
		} = settingsFlatData[`setting.${settingUUID}`];

		let value = null;

		if (setting) {
			if (name === 'project_number') {
				value = `${setting[type]}-${activeSystemFlatData.directory_number}`;
			} else {
				value = setting[type];
			}
		}

		const helpIcon = (
			<div className={style.settingHelpIcon} data-test-id="settingHelpIcon">
				<CustomIcon icon='question-circle' />
				<div className={style.settingHelpToolTip} data-test-id="settingHelpToolTip">
					{description && <div>{description}</div>}
					<div className={style.settingHelpToolTipName}>{name}</div>
				</div>
			</div>
		);

		let innerElm = null;

		const _numVal = value || 0;
		const numStr = (_numVal || '').toString().replace(/^0+/, '');
		const numVal = numStr || _numVal;

		switch (type) {
		case 'file':
		case 'string':
		case 'text':
			innerElm = (
				<input
					value={value || ''}
					disabled
				/>
			);

			break;
		case 'float':
		case 'int':
			innerElm = (
				<input
					type='number'
					value={numVal}
					disabled
				/>
			);

			break;
		default:
			console.error(`Setting type: ${type} won't display for System Group settings on setting: ${name}`);
			return '';
		}

		return (
			<div className={style.settingContainer} key={settingUUID} data-test-id={toSlug(name)}>
				<div className={style.settingLabel} data-test-id="settingLabel">
					{display_name || humanizeSettingName(name)}
					{helpIcon}
				</div>
				{innerElm}
			</div>
		);
	}

	getIconTypeValue(optionsList, value) {
		if (optionsList.length > 0) {
			for (let item of optionsList) {
				if (item.value === value) {
					return item;
				}
			}
		}
		return 0;
	}

	renderSetting(componentType, settingKey, settingData) {
		const {
			pendingUpdates,
			settingMap,
			activeSystemUUID,
			activeSystemFlatData,
		} = this.props;

		let {
			activeEntityKey,
			flatData,
			activeEntity,
			componentTypeToSettingMap,
		} = this.state;

		const {
			display_name,
			description,
			name,
		} = settingData;

		let {
			type,
		} = settingData;

		const key = `${activeEntityKey}.${settingKey}`;

		let innerElm = null;

		const customContentSidebarLabelUUID = settingMap?.['custom_content_sidebar_label']?.uuid;
		if (customContentSidebarLabelUUID && key === `setting.${customContentSidebarLabelUUID}`) {
			type = 'string';
		}

		const helpIcon = (
			<div className={style.settingHelpIcon} data-test-id="settingHelpIcon">
				<CustomIcon icon='question-circle' />
				<div className={style.settingHelpToolTip} data-test-id="settingHelpToolTip">
					{description && <div>{description}</div>}
					<div className={style.settingHelpToolTipName}>{name}</div>
				</div>
			</div>
		);

		if (componentType === 'entertainment' && settingData.name === 'categories') {
			return (
				<EntertainmentCategoryPicker
					key={key}
					settingMap={settingMap}
					activeEntityKey={activeEntityKey}
					settingKey={settingKey}
					flatData={flatData}
					updateSettingValue={this.updateSettingValue.bind(this)}
				/>
			);
		}

		if (componentType === 'brood' && settingData.name === 'is_visual_content_loop') {
			innerElm = (
				<label className={style.container}>
					<input
						type='checkbox'
						onChange={(e) => {
							this.updateSettingValue(settingKey, !value, type);

							if (e.target.checked) {
								let broodVisualContentLoopIDSettingUUID = settingMap['visual_content_loop_id']?.uuid;
								let broodVisualContentLoopID = activeEntity[`setting.${broodVisualContentLoopIDSettingUUID}`]?.int || 0;

								if (broodVisualContentLoopID > 0) {
									this.fetchBroodVisualContentLoopProviders(broodVisualContentLoopID, () => {
										this.setActiveEntity(activeEntity, activeEntityKey);
									});
								} else {
									this.setActiveEntity(activeEntity, activeEntityKey);
								}
							}
						}}
						checked={flatData[activeEntityKey]?.[settingKey]?.[type] || false}
					/>
					<span className={style.checkmark}></span>
				</label>
			);
		}

		if (componentType === 'brood' && settingData.name === 'visual_content_loop_id') {
			if (activeEntityKey.startsWith('component._')) {
				return (
					<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
						<div className={style.settingLabel} data-test-id="settingLabel">
							{display_name || humanizeSettingName(name)}
							{helpIcon}
						</div>
						<br />
						<div className={style.settingInputDisabled}>
							You must have a system to assign a VCL.
						</div>
					</div>
				);
			}

			// this checks for single brood as [m1Key] = { int: # }
			// and as multiple brood as [m1Key+'.int'] = #
			let m1Key = `${activeEntityKey}.${settingKey}`;
			let pendingUpdate = resolvePendingVCLID(pendingUpdates, m1Key);

			return <VisualContentLoopPicker
				key={key}
				settingMap={settingMap}
				activeEntityKey={activeEntityKey}
				settingKey={settingKey}
				flatData={flatData}
				updateSettingValue={(vclID) => {
					this.setState({
						flatData: {
							...flatData,
							[activeEntityKey]: {
								...flatData[activeEntityKey],
								[settingKey]: {
									int: vclID,
								},
							},
						},
					}, () => {
						this.fetchBroodVisualContentLoopProviders(vclID, () => {
							this.setActiveEntity(activeEntity, activeEntityKey);
						});
					});
				}}
				pendingUpdate={pendingUpdate}
			/>;
		}

		if (activeEntity.is_visual_content_loop_provider) {
			if ([ 'nova_size', 'peacock_size', 'vistar_size', 'monarch_size', ].includes(settingData.name)) {
				const {
					fd,
					accountUUID,
				} = this.props;


				let defaults = {};
				try {
					const accountM1Key = `account.${accountUUID}`;
					const accountVisualContentLoopProviders = fd[accountM1Key]?.visual_content_loop_providers || '{}';

					defaults = JSON.parse(accountVisualContentLoopProviders)?.settings || {};
				} catch (err) {
					console.error(err);
				}

				// VCL-level resolution wins over the account default (DEV-11111);
				// provider entity keys look like visual_content_loop_${vclID}::${type}::${id}::${systemUUID}
				let vclResolution = '';
				if (activeEntityKey.startsWith('visual_content_loop_')) {
					const vclID = activeEntityKey.split('::')[0].replace('visual_content_loop_', '');
					vclResolution = this.state.broodVisualContentLoopResolutions[vclID] || '';
				}

				settingData.account_value = vclResolution || defaults?.resolution || '';
			}
		}

		// this block gets the value for the icon_family dropdown so that icon_type has access to it
		let componentTypeKey = '';

		Object.keys(activeEntity).forEach((key) => {
			if (key.startsWith('component_type.')) {
				componentTypeKey = key;
			}
		});

		const componentTypeData = componentTypeToSettingMap[componentTypeKey];
		let familyValue = 0;
		for (let key in componentTypeData) {
			if (componentTypeData[key].name === 'icon_family') {
				familyValue = flatData[activeEntityKey]?.[key]?.[type] || 0;
				break;
			}
		}

		let value = flatData[activeEntityKey]?.[settingKey]?.[type];

		if (name === 'custom_content_sidebar_label') {
			value = flatData[activeEntityKey]?.custom_content_sidebar_label || '';
		}

		if (name === 'custom_tlc_builder_name' && !activeEntity.is_visual_content_loop_provider) {
			value = flatData[activeEntityKey]?.[settingKey]?.string || getComponentDisplayName(activeEntity.type) || '';
		}

		if (activeEntity.is_visual_content_loop_provider) {
			value = (flatData[activeEntityKey]?.settings || {})[activeSystemUUID]?.[settingKey];
			if (typeof value === 'undefined') {
				value = (activeEntity.settings || {})[activeSystemUUID]?.[settingKey];
				if (typeof value === 'undefined') {
					value = settingData.account_value || settingData.default_value;
				}
			}

			if (name === 'custom_tlc_builder_name' && (value === null || value === undefined || value === '')) {
				value = getComponentDisplayName(activeEntity.type) || '';
			}

			if (name === 'venue_id') {
				const {
					project_number,
				} = this.props.activeSystemGroup;

				const {
					directory_number,
				} = this.props.activeSystemFlatData;

				const defaultVenueID = `${project_number}-${directory_number}`;
				value = defaultVenueID;

				if (componentType === 'nova') {
					let customVenueID = activeSystemFlatData['nova_venue_id_val'] || '';
					if (customVenueID) {
						value = customVenueID;
					}
				}

				if (componentType === 'peacock' || componentType === 'vistar') {
					let customVenueID = activeSystemFlatData['vistar_venue_id_val'] || '';
					if (customVenueID) {
						value = customVenueID;
					}
				}
			}
		}

		if (exclusions.includes(name)) {
			return null;
		}

		if (Object.keys(componentTypeExclusions).includes(componentType) && componentTypeExclusions[componentType].includes(name)) {
			return null;
		}

		// If no value was found, add a null stub for the settingKey to flatData so
		// new settings can come through — but only when the setting object is
		// missing ENTIRELY (a new setting with no map row yet). Settings that
		// exist but hold a null value (the server omits the value attr) must be
		// left untouched — replacing them with a minimal {type: null} made
		// flatData differ from systemMap on every panel render, phantom-dirtying
		// the Save button after save. (DEV-11883)
		if (typeof value === 'undefined' && !flatData[activeEntityKey]?.[settingKey]) {
			// Intentionally mutate flatData to avoid infinite re-render loop.
			flatData[activeEntityKey] = {
				...flatData[activeEntityKey],
				[settingKey]: {
					[type]: null,
				},
			};
		}

		// Removed values
		if (unusedSettingsSpecific[componentType] && unusedSettingsSpecific[componentType].includes(name)) {
			return null;
		}
		if (unusedSettingsGeneric.includes(name)) {
			return null;
		}

		// Array values
		if (arrayNames.includes(name)) {
			let tmpVal = null;
			try {
				if (value) {
					tmpVal = JSON.parse(value);
				}
			} catch (err) {
				console.error(err);
			}

			if (!tmpVal) {
				tmpVal = [];
			}

			const stringVal = tmpVal.join(', ');
			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
					</div>
					<input
						onChange={(e) => {
							e.preventDefault();
							let arrayVal = [];
							if (e.target.value.length > 0) {
								arrayVal = e.target.value.split(', ');
							}
							this.updateSettingValue(settingKey, JSON.stringify(arrayVal), type);
						}}
						value={stringVal}
					/>
				</div>
			);
		}

		let isReadOnlySetting = readOnlySettingsSpecific[componentType] && readOnlySettingsSpecific[componentType].includes(name);
		if (name === 'venue_id') {
			if (!activeEntity.is_visual_content_loop_provider) {
				isReadOnlySetting = false;
			}
		}

		// Multiple selection values
		if (multipleSelectionValues[componentType] && multipleSelectionValues[componentType][name]) {
			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
					</div>
					<label className={`${style.container} ${style.selectContainer}`}>
						<Select
							isMulti={true}
							allowSelectAll={true}
							{...multiSelectSummaryProps}
							maxMenuHeight={180}
							menuPlacement="auto"
							className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
							styles={multiSummaryDropdownStyles}
							dropdownIndicatorTestId={`indicator-${name}`}
							optionTestIdPrefix="option"
							options={multipleSelectionValues[componentType][name]}
							onChange={(e) => {
								const newValue = e.map((item) => {
									return item.value;
								}).join(',');
								this.updateSettingValue(settingKey, newValue, type);
							}}
							value={multipleSelectionValues[componentType][name].filter((option) => {
								return (value || '').split(',').includes(option.value);
							})}
						/>
					</label>
				</div>
			);
		}

		if (dropdownSettings[componentType] && dropdownSettings[componentType].includes(name) && name === 'icon_family') {
			return (
				<React.Fragment key={key}>
					<div className={style.settingContainer} data-test-id={toSlug(name)}>
						<div className={style.settingLabel} data-test-id="settingLabel" title={name}>
							{display_name || humanizeSettingName(name)}
							{helpIcon}
						</div>
						<label className={`${style.container} ${style.selectContainer}`}>
							<Select
								className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
								styles={dropdownStyles}
								components={selectComponents}
								dropdownIndicatorTestId={`indicator-${name}`}
								optionTestIdPrefix="option"
								options={dropdownValues[componentType][name]}
								onChange={(e) => {
									familyValue = e.value;
									this.updateSettingValue(settingKey, e.value, type);
								}}
								value={dropdownValues[componentType][name].find((option) => {
									return option.value === value;
								})}
							/>
						</label>
					</div>

					{/* Uploader row for Upload Image family (value: 3) — own grid row below the dropdown */}
					{Number(familyValue) === 3 && (() => {
						const entityData = this.state.flatData?.[activeEntityKey] || {};
						const nameKey = this.props.settingMap?.icon_upload_name?.uuid ? `setting.${this.props.settingMap.icon_upload_name.uuid}` : null;
						const urlKey = this.props.settingMap?.icon_upload?.uuid ? `setting.${this.props.settingMap.icon_upload.uuid}` : null;
						// Guard the key access: nameKey/urlKey are null when the setting UUIDs
						// are missing, and entityData[null] would coerce to the 'null' string key.
						const uploadedName = (nameKey && entityData[nameKey]?.string)
							|| (urlKey && entityData[urlKey]?.string ? entityData[urlKey].string.split('/').pop() : '')
							|| '';
						return (
							// Same label/field structure as the sibling settings: an empty label cell
							// keeps the field column aligned in the two-column grids.
							<div className={`${style.settingContainer} ${style.iconUploaderRow}`} data-test-id="icon-upload-row">
								<div className={style.settingLabel} aria-hidden="true" />
								<div className={style.iconUploader}>
									<div
										className={`${style.uploaderFileName} ${uploadedName ? '' : style.uploaderFileNameEmpty}`}
										title={uploadedName || undefined}
										data-test-id="icon-upload-filename"
									>
										{uploadedName || 'No image uploaded'}
									</div>
									<div className={style.uploaderButtons}>
										<button
											type="button"
											onClick={(e) => { e.preventDefault(); document.getElementById(`iconUploadButton-${activeEntityKey}`)?.click(); }}
											className="button"
											title="Upload image"
											data-test-id="icon-upload-browse"
										>
											<CustomIcon icon='cloud-upload-alt' />
										</button>
										<button
											type="button"
											onClick={(e) => { e.preventDefault(); if (uploadedName) this.deleteIcon(activeEntityKey); }}
											className="button"
											title={uploadedName ? 'Remove image' : 'No image to remove'}
											data-test-id="icon-upload-delete"
											disabled={!uploadedName}
										>
											<CustomIcon icon='trash-alt' />
										</button>
									</div>
									<input
										id={`iconUploadButton-${activeEntityKey}`}
										onChange={(e) => { this.handleIconUpload(e, activeEntityKey); e.target.value = ''; }}
										accept='.png,.svg'
										type='file'
										style={{ display: 'none' }}
									/>
								</div>
							</div>
						);
					})()}
				</React.Fragment>
			);
		}
		if (dropdownSettings[componentType] && dropdownSettings[componentType].includes(name) && name === 'icon_type') {
			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
					</div>
					<label className={`${style.container} ${style.selectContainer}`}>
						<Select
							className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
							styles={dropdownStyles}
							components={selectComponents}
							dropdownIndicatorTestId={`indicator-${name}`}
							optionTestIdPrefix="option"
							options={dropdownValues[componentType]['icon_family'][familyValue][name]}
							onChange={(e) => {
								this.updateSettingValue(settingKey, e.value, type);
							}}
							value={this.getIconTypeValue(dropdownValues[componentType]['icon_family'][familyValue][name], value)}
							formatOptionLabel={(option) => {
								return (
									<div className={style.selectOptionWithIcon}>
										{option.icon && (
											<span className={style.optionIcon}>
												{this.renderIcon(option.icon)}
											</span>
										)}
										<span className={style.optionLabel}>{option.label}</span>
									</div>
								);
							}}
						/>
					</label>
				</div>
			);
		}

		// Custom fonts dropdown menu
		if (name === 'font_header' || name === 'font_body') {
			// Helper function to find option in grouped structure
			const findOptionInGroups = (groups, targetValue) => {
				for (const group of groups) {
					const foundOption = group.options.find((option) => {
						return option.value === targetValue;
					});
					if (foundOption) {
						return foundOption;
					}
				}
				return null;
			};

			return (
				<div className={`${style.settingContainer} ${style.settingWide}`} key={key} data-test-id={toSlug(name)}>
					<div>
						<div className={style.settingLabel} data-test-id="settingLabel">
							{display_name || humanizeSettingName(name)}
							{helpIcon}
						</div>
						<div className={style.fontNote}>
							Layouts not optimized for alternate fonts. Please investigate the effects of these changes after choosing, and adjust with CSS accordingly.
						</div>
					</div>
					<label className={`${style.container} ${style.selectContainer}`}>
						<Select
							className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
							styles={dropdownStyles}
							components={selectComponents}
							dropdownIndicatorTestId={`indicator-${name}`}
							optionTestIdPrefix="option"
							options={dropdownValues[componentType][name]}
							onChange={(e) => {
								this.updateSettingValue(settingKey, e.value, type);
							}}
							value={findOptionInGroups(dropdownValues[componentType][name], value)}
							formatOptionLabel={(option) => {
								return (
									<div className={style.fontOption} style={{
										fontFamily: option.value,
									}}>
										{option.label}
									</div>
								);
							}}
							formatGroupLabel={(group) => {
								return (
									<div className={style.fontGroup}>
										{group.label}
									</div>
								);
							}}
						/>
					</label>
				</div>
			);
		}

		// standard Dropdown values
		if (dropdownSettings[componentType] && dropdownSettings[componentType].includes(name)) {
			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
						{settingData.account_value && (
							<div>
								Account Default: {settingData.account_value.toString()}
							</div>
						)}
					</div>
					<label className={`${style.container} ${style.selectContainer}`}>
						<Select
							isDisabled={isReadOnlySetting}
							className={style.buttonsDropdown + ' ' + style.autoWidthSelect}
							styles={dropdownStyles}
							components={selectComponents}
							dropdownIndicatorTestId={`indicator-${name}`}
							optionTestIdPrefix="option"
							options={dropdownValues[componentType][name]}
							onChange={(e) => {
								this.updateSettingValue(settingKey, e.value, type);
							}}
							value={dropdownValues[componentType][name].find((option) => {
								return option.value === value;
							})}
						/>
					</label>
				</div>
			);
		}

		// Custom toggle
		if (name === 'combined') {
			let nestedIsChecked = false;
			if (value === false) {
				nestedIsChecked = true;
			}

			let combinedIsChecked = false;
			if (value === true) {
				combinedIsChecked = true;
			}

			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
					</div>
					<div className={style.customSwitch}>
						<div>
							<input
								type='radio'
								label='Nested'
								onChange={(e) => {
									this.updateSettingValue(settingKey, false, type);
								}}
								checked={nestedIsChecked}
							/>

							<label
								onClick={(e) => {
									this.updateSettingValue(settingKey, false, type);
								}}
							>
								<span>Nested</span>
							</label>
						</div>
						<div>
							<input
								type='radio'
								label='Combined'
								onChange={(e) => {
									this.updateSettingValue(settingKey, true, type);
								}}
								checked={combinedIsChecked}
							/>

							<label
								onClick={(e) => {
									this.updateSettingValue(settingKey, true, type);
								}}
							>
								<span>Combined</span>
							</label>
						</div>
					</div>
				</div>
			);
		}

		if (isReadOnlySetting && this.props.category) {
			let inputValue = '';
			if (name === 'condor_configuration') {
				inputValue = this.props?.config?.display_name || '';
			} else if (name === 'condor_theme') {
				inputValue = this.props?.theme?.display_name || '';
			} else {
				inputValue = this.props?.category?.display_name || '';
			}

			return (
				<div className={style.settingContainer} key={key} data-test-id={toSlug(name)}>
					<div className={style.settingLabel} data-test-id="settingLabel">
						{display_name || humanizeSettingName(name)}
						{helpIcon}
					</div>
					<input
						disabled={isReadOnlySetting}
						value={inputValue}
					/>
				</div>

			);
		}

		let textMaxLength;
		if (this.state.activeEntity.description === 'emergency_message' && display_name === 'Title') {
			textMaxLength = 20;
		} else if (this.state.activeEntity.description === 'emergency_message' && display_name === 'Message') {
			textMaxLength = 140;
		}


		let _numVal = value || 0;
		const extraNumProps = {};
		if (numberRanges[componentType] && numberRanges[componentType][name]) {
			extraNumProps.max = numberRanges[componentType][name].max;
			extraNumProps.min = numberRanges[componentType][name].min;
			if (_numVal > extraNumProps.max) {
				_numVal = extraNumProps.max;
			}
			if (_numVal < extraNumProps.min) {
				_numVal = extraNumProps.min;
			}
		}

		const numStr = (_numVal || '').toString().replace(/^0+/, '');

		const numVal = numStr || _numVal || '';

		if (!innerElm) {
			switch (type) {
			case 'bool':
				innerElm = (
					<label className={style.container}>
						<input
							type='checkbox'
							onChange={(e) => {
								this.updateSettingValue(settingKey, !value, type);
							}}
							checked={value}
						/>
						<span className={style.checkmark}></span>
					</label>
				);

				break;
			case 'date':
			case 'datetime':
			case 'timestamp':
				if (value) {
					value = new Date(value);
					// Set value to null if invalid date object
					if (isNaN(Date.parse(value))) {
						value = null;
					}
				}

				innerElm = (
					<DatePicker
						selected={value}
						onChange={(date) => {
							let formattedDate = null;
							if (date) {
								formattedDate = date.toISOString();
							}
							this.updateSettingValue(settingKey, formattedDate, type);
						}}
						{...type === 'date' ? null : {
							showTimeSelect: true,
						}}
						// showTimeSelect
						timeFormat='h:mm aa'
						timeIntervals={15}
						dateFormat='MMMM d, yyyy h:mm aa'
						dropdownMode='select'
						showMonthDropdown
						showYearDropdown
						isClearable
					/>
				);

				break;
			case 'file':
			case 'string':
			case 'text': {
				let inputErrorMessage = '';

				if (name === 'source' && !value.toLowerCase().startsWith('https://') && value) {
					inputErrorMessage = 'URL must start with https://';
				}

				// Check if this is a color field
				const isColorField = name === 'theme_color_primary' || name === 'theme_color_secondary' || name === 'theme_color_tertiary' || name === 'theme_font_color_primary' || name === 'theme_font_color_secondary' || name === 'theme_button_color' || name === 'theme_iconFilter_color';

				if (isColorField) {
				// Color field with color picker
					innerElm = (
						<>
							<div className={style.colorInputContainer}>
								<input
									type="color"
									value={value || '#000000'}
									onChange={(colorEvent) => {
										this.updateSettingValue(settingKey, colorEvent.target.value, type);
									}}
									className={style.colorPicker}
									title="Color picker"
								/>
								<input
									disabled={isReadOnlySetting}
									onChange={(e) => {
										e.preventDefault();

										let updateBundle = false;
										// Check for _bundle records to update when a custom_content_sidebar_label is changed.
										if (name === 'custom_content_sidebar_label') {
											updateBundle = true;
										}
										if (name === 'source') {
											if (e.target.value.toLowerCase().startsWith('https://') || e.target.value === '') {
												e.target.classList.remove('invalidValue');
												inputErrorMessage = '';
											} else {
												e.target.classList.add('invalidValue');
												inputErrorMessage = 'URL must start with https://';
											}
										}
										this.updateSettingValue(settingKey, e.target.value, type, updateBundle);
									}}
									value={value || ''}
									maxLength={textMaxLength}
									className={style.colorTextInput}
								/>

							</div>
							{inputErrorMessage && <div className={style.inputErrorMessage}>{inputErrorMessage}</div>}
						</>
					);
				} else {
					// Regular text field (original structure)
					innerElm = (
						<>
							<input
								disabled={isReadOnlySetting}
								onChange={(e) => {
									e.preventDefault();

									let updateBundle = false;
									// Check for _bundle records to update when a custom_content_sidebar_label is changed.
									if (name === 'custom_content_sidebar_label') {
										updateBundle = true;
									}
									if (name === 'source') {
										if (e.target.value.toLowerCase().startsWith('https://') || e.target.value === '') {
											e.target.classList.remove('invalidValue');
											inputErrorMessage = '';
										} else {
											e.target.classList.add('invalidValue');
											inputErrorMessage = 'URL must start with https://';
										}
									}
									this.updateSettingValue(settingKey, e.target.value, type, updateBundle);
								}}
								value={value || ''}
								maxLength={textMaxLength}
							/>
							{inputErrorMessage && <div className={style.inputErrorMessage}>{inputErrorMessage}</div>}
						</>
					);
				}

				break;
			}
			case 'float':
			case 'int':
				innerElm = (
					<>
						<input
							type='number'
							placeholder={0}
							min='-Infinity' max='Infinity'
							maxLength={textMaxLength}
							value={numVal}
							step='any'
							onChange={(e) => {
								let val = Number(e.target.value);
								this.updateSettingValue(settingKey, val, type);
							}}
							onBlur={(e) => {
								let val = Number(e.target.value);
								this.updateSettingValue(settingKey, val, type);
							}}
							{...extraNumProps}
						/>
					</>
				);

				break;
			case 'json':
				innerElm = (
					<JSONField
						settingKey={settingKey}
						type={type}
						defaultValue={jsonTypes[name]}
						updateSettingValue={this.updateSettingValue.bind(this)}
						value={value}
					/>
				);

				break;
			case 'time':
				if (value) {
					// Create date object for react-datepicker
					let newDate = new Date();

					let time = value.split(':');
					let hour = time[0];
					let minutes = time[1];
					let seconds = time[2];

					newDate.setHours(hour);
					newDate.setMinutes(minutes);
					newDate.setSeconds(seconds);
					value = newDate;

					// Set value to empty if invalid date object
					if (isNaN(Date.parse(value))) {
						console.error('invalid date format');
						value = null;
					}
				}

				innerElm = (
					<DatePicker
						selected={value}
						onChange={(time) => {
							let formattedTime = null;
							if (time) {
								formattedTime = time.toLocaleTimeString('it-IT');
							}
							this.updateSettingValue(settingKey, formattedTime, type);
						}}
						showTimeSelect
						showTimeSelectOnly
						timeFormat="h:mm aa"
						timeIntervals={15}
						dateFormat="h:mm aa"
						isClearable
					/>
				);

				break;
			default:
				console.error(`BAD SETTING_TYPE: ${type} FOR SETTING: ${name}`);
				return '';
			}
		}

		let customCSSUploader = null;
		if (name === 'custom_css') {
			const {
				customCSSOptions,
			} = this.state;

			let fileName = value;
			let downloadButton = 'button button-disabled no-click';
			let uploadButton = 'button';
			if (value) {
				let filePath = value.split('/');
				if (filePath[2]) {
					fileName = filePath[2];
				}
				downloadButton = 'button';
			}

			customCSSUploader = (
				<div className={`uploader-buttons ${style.uploader}`}>
					<Select
						options={customCSSOptions}
						className={style.uploaderDropdown}
						styles={dropdownStyles}
						components={selectComponents}
						dropdownIndicatorTestId="indicator-customCSS"
						optionTestIdPrefix="option"
						onChange={(e) => {
							const {
								accountUUID,
							} = this.props;
							const newVal = `${vultureENV}/${accountUUID}/${e.value}`;
							this.updateSettingValue(settingKey, newVal, type);
						}}
						value={{
							value: fileName,
							label: fileName,
						}}
					/>
					<div className={style.uploaderButtons}>
						<button
							onClick={(e) => {
								e.preventDefault();
								document.getElementById('customCSSUploadButton').click();
							}}
							className={uploadButton}
							data-testing-info={buildTestData('button--uploadCSS')}
						>
							<CustomIcon icon='cloud-upload-alt' />
						</button>
						<button
							onClick={(e) => {
								e.preventDefault();
								downloadCSS(this.state.bucketName, value);
							}}
							className={downloadButton}
							data-test-id='downloadCSSButton'
						>
							<CustomIcon icon='cloud-download-alt' />
						</button>
						<button
							onClick={(e) => {
								e.preventDefault();
								this.setState({
									confirmModal:        true,
									confirmModalContent:
										<>
											<p>{'This .css will not be added to this system anymore. Do you want to continue?'}</p>
											<div className='confirmModalButtonContainer'>
												<div className='confirm-modal-buttons'>
													<button className='button' onClick={(e) => {
														this.setState({
															confirmModal: false,
														});
														this.deleteCSS(settingKey, type);
													}}>Okay</button>
												</div>
												<div className='confirm-modal-buttons'>
													<button className={`button ${style.cancelButton}`} onClick={() => {
														this.setState({
															confirmModal: false,
														});
													}}>Cancel</button>
												</div>
											</div>
										</>,
								});
							}}
							className={downloadButton}
							data-test-id='deleteCSSButton'
						>
							<CustomIcon icon='trash' />
						</button>
					</div>
					<input
						id='customCSSUploadButton'
						data-test-id='customCSSUploadInput'
						onChange={(e) => {
							if (e.target.files[0].name === fileName) {
								let fileRef = e;
								this.setState({
									confirmModal:        true,
									confirmModalContent:
										<>
											<p>{`${fileName} already exists, are you sure you want to overwrite it?  It may affect other systems in this account.`}</p>
											<div className='confirmModalButtonContainer'>
												<div className='confirm-modal-buttons'>
													<button className='button' onClick={(e) => {
														this.setState({
															confirmModal: false,
														});
														this.handleCustomCSSUpload(fileRef, settingKey, type);
														fileRef.target.value = '';
													}}>Okay</button>
												</div>
												<div className='confirm-modal-buttons'>
													<button className={`button ${style.cancelButton}`} onClick={(e) => {
														this.setState({
															confirmModal: false,
														});
														fileRef.target.value = '';
														fileName = '';
													}}>Cancel</button>
												</div>
											</div>
										</>,
								});
							} else {
								this.handleCustomCSSUpload(e, settingKey, type);
								e.target.value = '';
							}
						}}
						accept='.css'
						type='file'
					/>
				</div>
			);
		}

		if (name === 'custom_inline_css') {
			innerElm = (
				<div>
					<style>{`
						.ace_editor_custom_css,
						.ace_editor_custom_css * {
							font-family: "Courier New", Courier, monospace !important;
							font-size: 14px !important;
						}
					`}</style>
					<AceEditor
						mode='css'
						theme='monokai'
						onChange={(val) => {
							this.updateSettingValue(settingKey, val, type);
						}}
						onLoad={(editor) => {
							editor.container.classList.add('ace_editor_custom_css');
							editor.setFontSize(14);
						}}
						name='custom_inline_css'
						value={value || ''}
						editorProps={{
							$blockScrolling: true,
						}}
						setOptions={{
							enableBasicAutocompletion: true,
							enableLiveAutocompletion:  true,
							enableSnippets:            true,
						}}
						maxLines={20}
						minLines={20}
						width='100%'
						showPrintMargin={false}
						fontSize='0.875em'
					/>
				</div>
			);
		}

		if (name === 'theme_color_primary' || name === 'theme_color_secondary' || name === 'theme_color_tertiary' || name === 'theme_font_color_primary' || name === 'theme_font_color_secondary' || name === 'theme_button_color' || name === 'theme_iconFilter_color') {
			const templateColorMap = {
				exp1:      [ 'theme_color_primary', 'theme_font_color_primary', 'theme_iconFilter_color',  ],
				static3:   [ 'theme_color_primary', 'theme_color_secondary', 'theme_font_color_primary', 'theme_font_color_secondary', 'theme_iconFilter_color', ],
				touch3int: [ 'theme_color_primary', 'theme_color_secondary', 'theme_color_tertiary', 'theme_font_color_primary', 'theme_font_color_secondary', 'theme_button_color', 'theme_iconFilter_color', ],
			};

			if (componentType === 'top_level_condor') {
				let templateSettingUUID = '';

				for (let key in componentTypeData) {
					if (componentTypeData[key].name === 'condor_configuration') {
						templateSettingUUID = key;
						break;
					}
				}

				// Try to get template config from multiple possible sources
				let templateConfig = flatData[activeEntityKey]?.[templateSettingUUID]?.string;

				// If not found in flatData (empty string), check if we have it from props (TemplateBuilder context)
				if (!templateConfig && this.props.config?.display_name) {
					templateConfig = this.props.config.display_name;
				}

				// Only show theme colors for valid template configurations (exp1, static3, touch3int)
				// If templateConfig is not one of these, hide all theme colors
				if (!templateConfig || !templateColorMap[templateConfig]) {
					return;
				}

				// If we have a valid config, only show the colors allowed for that config
				if (!templateColorMap[templateConfig].includes(name)) {
					return;
				}
			}
		}

		const isWideSetting = checkIsWideSetting(settingData);
		const settingClasses = `${style.settingContainer} ${isWideSetting ? style.settingWide : ''}`;

		return (
			<div className={settingClasses} key={key} data-test-id={toSlug(name)}>
				<div className={style.settingLabel} data-test-id="settingLabel">
					{display_name || humanizeSettingName(name)}
					{helpIcon}
				</div>
				{customCSSUploader || innerElm}
			</div>
		);
	}

	renderCategorizedSettings(categoryMap, sortedData, hiddenSettings, componentType, excludedNames = []) {
		const allCategorizedNames = [
			...Object.values(categoryMap).flatMap((cat) => {
				return cat.settings;
			}),
			...excludedNames,
		];

		const uncategorizedSettings = Object.entries(sortedData).filter(([ , val, ]) => {
			return !allCategorizedNames.includes(val.name) &&
				!hiddenSettings.includes(val.name);
		});

		return (
			<>
				{Object.entries(categoryMap).map(([ categoryName, categoryConfig, ]) => {
					const categorySettings = Object.entries(sortedData).filter(([ , val, ]) => {
						return categoryConfig.settings.includes(val.name) &&
							!hiddenSettings.includes(val.name) &&
							!excludedNames.includes(val.name);
					});

					if (categorySettings.length === 0) {
						return null;
					}

					return (
						<div className={style.staticSettingsCategoryContainer} key={categoryName}>
							<div className={style.staticSettingsCategoryHeader}>
								<CustomIcon icon={categoryConfig.icon} className={style.staticSettingsCategoryIcon} />
								<span>{categoryName}</span>
							</div>
							<div className={style.staticSettingsCategoryGrid}>
								{categorySettings.sort(sortWideSettingsToBottom).map(([ key, val, ]) => {
									return this.renderSetting(componentType, key, val);
								})}
							</div>
						</div>
					);
				})}
				{uncategorizedSettings.length > 0 && (
					<div className={style.staticSettingsCategoryContainer}>
						<div className={style.staticSettingsCategoryHeader}>
							<CustomIcon icon="cog" className={style.staticSettingsCategoryIcon} />
							<span>Component Settings</span>
						</div>
						<div className={style.staticSettingsCategoryGrid}>
							{uncategorizedSettings.sort(sortWideSettingsToBottom).map(([ key, val, ]) => {
								return this.renderSetting(componentType, key, val);
							})}
						</div>
					</div>
				)}
			</>
		);
	}

	handleUUIDLink(e) {
		e.preventDefault();

		let entityKeys = matchFDKeys(this.props.fd, `system.${this.props.activeSystemUUID}.mac_address`);

		if (entityKeys.length > 0) {
			alert('This system may have shipped, please be aware that any changes you make to CONTENT will display on the shipped system and its display');
		}

		const uuid = this.state.activeEntityKey.split('.')[1];
		window.open(`${previewHost}/?condor_component=${uuid}`, '_blank');
	}

	getInfoValues() {
		let prettifiedOrientation = '';

		for (const [ m1Key, m1Val, ] of Object.entries(this.props.activeSystemFlatData)) {
			if (m1Key.startsWith('setting.')) {
				const orientation = m1Val.string;
				if ([ 'portraitleft', 'portraitright', 'landscape', 'landscapeflipped', ].includes(orientation)) {
					switch (orientation) {
					case 'portraitleft':
						prettifiedOrientation = 'Portrait (left)';
						break;
					case 'portraitright':
						prettifiedOrientation = 'Portrait (right)';
						break;
					case 'landscape':
						prettifiedOrientation = 'Landscape';
						break;
					case 'landscapeflipped':
						prettifiedOrientation = 'Landscape (flipped)';
						break;
					default:
						prettifiedOrientation = orientation;
						break;
					}
					break;
				}
			}
		}

		const infoValues = {
			orientation: prettifiedOrientation ? prettifiedOrientation : '',
			resolution:  this.props.activeSystemFlatData.resolution ? this.props.activeSystemFlatData.resolution : '',
		};

		return infoValues;
	}

	renderTLCUUID() {
		const {
			activeEntity,
			activeEntityKey,
		} = this.state;

		if (activeEntity.type === 'top_level_condor') {
			const uuid = activeEntityKey.split('.')[1];

			if (uuid && uuid.length === 36) {
				const infoValues = this.getInfoValues();

				return (
					<div>
						<div className={style.settingContainer} data-test-id="uuid">
							<div className={style.settingLabel} data-test-id="settingLabel">UUID (click the link to see a LiveView):</div>
							<a href='#' onClick={(e) => {
								this.handleUUIDLink(e);
							}} className={style.settingLabel} data-test-id="settingLabel">{uuid}</a>
							{infoValues.orientation && (
								<div className={style.systemInfoContainer}>
									<div className={style.settingLabel} data-test-id="settingLabel">Orientation:</div>
									<div className={style.settingValue}>{infoValues.orientation}</div>
								</div>
							)}
							{infoValues.resolution && (
								<div className={style.systemInfoContainer}>
									<div className={style.settingLabel} data-test-id="settingLabel">Resolution:</div>
									<div className={style.settingValue}>{infoValues.resolution}</div>
								</div>
							)}
						</div>
					</div>
				);
			}
		}

		return;
	}

	// Not a real DB setting/component_type_to_setting_map row — mock_nova is a
	// testing-only knob on the real 'nova' VCL provider, gated on the SAME
	// account-level toggle Account Settings already exposes (VCLSettings.js
	// advertisingFeeds 'Mock Nova' entry / defaultVCLProvidersJSON.advertising.mock_nova),
	// not a separate flag.
	getMockNovaEnabledForAccount() {
		const {
			fd,
			accountUUID,
		} = this.props;

		try {
			const accountM1Key = `account.${accountUUID}`;
			const accountVisualContentLoopProviders = fd[accountM1Key]?.visual_content_loop_providers || '{}';

			return !!JSON.parse(accountVisualContentLoopProviders)?.advertising?.mock_nova?.enabled;
		} catch (err) {
			console.error(err);

			return false;
		}
	}

	renderSettings() {
		const {
			activeEntityKey,
			activeEntity,
			componentTypeToSettingMap,
			flatData,
		} = this.state;

		const {
			activeSystemUUID,
		} = this.props;

		const {
			display_name, type,
		} = activeEntity || {};

		const hiddenSettings = [
			'raven_provider_token',
			'raven_provider',
			'raven_theme',
			'raven_ts_branding',
			'raven_client_branding',
			'raven_ts_branding_color',
			'time_zone',
			'icon_upload',
			'icon_upload_name',
		];

		let componentTypeKey = '';
		Object.keys(activeEntity).forEach((key) => {
			if (key.startsWith('component_type.')) {
				componentTypeKey = key;
			}
		});

		let componentTypeData = componentTypeToSettingMap[componentTypeKey];
		if (activeEntity.is_visual_content_loop_provider) {
			componentTypeData = Object.assign({}, Object.values(componentTypeToSettingMap).find((map) => {
				if (activeEntity.type === 'slide_deck') {
					return map.type === 'pelican';
				}
				if (activeEntity.type === 'peacock' || activeEntity.type === 'vistar') {
					return map.type === 'peacock';
				}

				return map.type === activeEntity.type || map.type === `monarch_feed_${activeEntity.type}`;
			}));

			componentTypeData.isVisualContentLoopProvider = true;
		}

		let sortedData = Object.entries(componentTypeData)
			.filter(([ key, val, ]) => {
				return key.startsWith('setting.');
			})
			.sort(([ , aVal, ], [ , bVal, ]) => {
				if (aVal.name > bVal.name) {
					return 1;
				}

				if (aVal.name < bVal.name) {
					return -1;
				}

				return 0;
			})
			.reduce((obj, [ key, val, ]) => {
				if (componentTypeData.isVisualContentLoopProvider) {
					key = val.name;
				}
				obj[key] = val;

				return obj;
			}, {});

		// mock_nova is a fake, non-schema field on the real 'nova' provider —
		// see getMockNovaEnabledForAccount(). Only shown when the account opts in,
		// and the scenario field only once mocking itself is enabled.
		// is_visual_content_loop_provider is required, not incidental:
		// updateSettingValue() only persists by-name into settings[systemUUID]
		// for VCL providers, so on a plain nova component the controls would
		// render and silently no-op.
		if (activeEntity.type === 'nova'
			&& activeEntity.is_visual_content_loop_provider
			&& this.getMockNovaEnabledForAccount()) {
			sortedData.mock_nova_enabled = {
				name: 'mock_nova_enabled',
				display_name: 'Enable Mock Nova',
				type: 'bool',
			};

			const mockNovaEnabledValue = (flatData[activeEntityKey]?.settings || {})[activeSystemUUID]?.mock_nova_enabled
				?? (activeEntity.settings || {})[activeSystemUUID]?.mock_nova_enabled;

			if (mockNovaEnabledValue) {
				sortedData.mock_nova_scenario = {
					name:         'mock_nova_scenario',
					display_name: 'Mock Nova Scenario',
					description:  'Full = always full, never zero (default). Others simulate partial/no ad/network-down responses for testing.',
					type:         'string',
				};

				sortedData.mock_nova_duration_seconds = {
					name:         'mock_nova_duration_seconds',
					display_name: 'Mock Nova Duration (seconds)',
					description:  'Overrides ad duration for faster manual/e2e testing cycles. Leave blank to use the normal static_duration/default (5s).',
					type:         'int',
				};
			}
		}

		// Build custom field for listing_collection
		let listingCustomField = null;
		if (activeEntity.type === 'listing_collection') {
			listingCustomField = this.buildListingCollectionCustomField();
		}

		if (activeEntity.type === 'listings_scroller') {
			listingCustomField = this.buildListingsScrollerCustomField();
		}

		// Build custom field for listing_group
		if (activeEntity.type === 'listing_group') {
			let listingGroupEntityKey = '';
			for (const entityKey of Object.keys(activeEntity)) {
				if (entityKey.startsWith('listing_group.')) {
					listingGroupEntityKey = entityKey;
					break;
				}
			}

			listingCustomField = (
				<ListingGroupCustomField
					app={this.props.app}
					accountUUID={this.props.accountUUID}
					activeEntity={activeEntity}
					deleteEntityFromFlatData={this.deleteEntityFromFlatData.bind(this)}
					flatData={JSON.parse(JSON.stringify(flatData))}
					listingGroupEntityKey={listingGroupEntityKey}
					stateCallback={this.stateCallback.bind(this)}
					updateSettingValue={this.updateSettingValue.bind(this)}
				/>
			);
		}

		if (activeEntity.type === 'brood') {
			let broodSettingMap = {};
			for (const [ key, val, ] of Object.entries(sortedData)) {
				broodSettingMap[val.name] = key;
			}

			const isVisualContentLoopSettingM1Key = broodSettingMap['is_visual_content_loop'];
			const visualContentLoopIDSettingM1Key = broodSettingMap['visual_content_loop_id'];
			// custom_tlc_builder_name is also kept visible in VCL mode so the
			// brood can be given a human-readable label ("Left VCL", "Right VCL")
			// that surfaces on the Loop Assignments page when a system has more
			// than one VCL brood (DEV-11279).
			const customTLCBuilderNameSettingM1Key = broodSettingMap['custom_tlc_builder_name'];

			const pendingUpdates = this.props.pendingUpdates || {};
			const pendingIsVisualContentLoop = pendingUpdates[activeEntityKey + '.' + isVisualContentLoopSettingM1Key]?.bool || false;

			const isVisualContentLoop = activeEntity[isVisualContentLoopSettingM1Key]?.bool || false;
			if (isVisualContentLoop || pendingIsVisualContentLoop) {
				const newSortedData = {
					[isVisualContentLoopSettingM1Key]: sortedData[isVisualContentLoopSettingM1Key],
					[visualContentLoopIDSettingM1Key]: sortedData[visualContentLoopIDSettingM1Key],
				};
				if (customTLCBuilderNameSettingM1Key && sortedData[customTLCBuilderNameSettingM1Key]) {
					newSortedData[customTLCBuilderNameSettingM1Key] = sortedData[customTLCBuilderNameSettingM1Key];
				}
				sortedData = newSortedData;
			} else {
				Reflect.deleteProperty(sortedData, visualContentLoopIDSettingM1Key);
			}
		}

		return (
			<div className={style.dataContainer}>
				<div className={style.skeletonContainerHeader}>
					<div className={style.headerLabel}>{getComponentDisplayName(type) || display_name}</div>
				</div>
				<div className={style.scrollingWrapper}>
					{this.renderTLCUUID()}
					{listingCustomField}
					{readOnlySystemGroupSettingsSpecific[activeEntity.description] &&
						readOnlySystemGroupSettingsSpecific[activeEntity.description].map((setting) => {
							return this.renderSystemGroupSettings(setting);
						})
					}

					{activeEntity.type === 'listing_group' ? (
						<>
							<div
								className={style.staticListingSettingsHeader}
								role="button"
								tabIndex={0}
								aria-expanded={this.state.staticListingSettingsExpanded}
								onClick={(e) => {
									// Don't collapse/expand when the user is selecting header text to copy it.
									const selection = window.getSelection();
									if (selection && !selection.isCollapsed && e.currentTarget.contains(selection.anchorNode)) {
										return;
									}
									return this.setState({
										staticListingSettingsExpanded: !this.state.staticListingSettingsExpanded,
									});
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										this.setState({
											staticListingSettingsExpanded: !this.state.staticListingSettingsExpanded,
										});
									}
								}}
							>
								<span className={style.staticListingSettingsHeaderLabel}>Static Listing Settings</span>
								<CustomIcon
									icon="chevron-down"
									className={`${style.staticListingSettingsChevron} ${this.state.staticListingSettingsExpanded ? style.staticListingSettingsChevronExpanded : ''}`}
								/>
							</div>
							<div className={`${style.staticListingSettingsContent} ${this.state.staticListingSettingsExpanded ? style.staticListingSettingsContentExpanded : ''}`}>
								{this.renderCategorizedSettings(
									LISTING_GROUP_SETTING_CATEGORIES,
									sortedData,
									hiddenSettings,
									activeEntity.description || activeEntity.type
								)}
							</div>
						</>
					) : (
						(() => {
							// Compute componentType once for all sections
							let componentType = activeEntity.description || activeEntity.type;
							if (activeEntity.is_visual_content_loop_provider) {
								if (activeEntity.type === 'slide_deck') {
									componentType = 'pelican';
								}
								if (activeEntity.type === 'vistar') {
									componentType = 'peacock';
								}
								if (activeEntity?.category === 'infotainment') {
									componentType = `monarch_feed_${activeEntity.type}`;
								}
							}

							// Pre-apply brood_enable_transition defaults
							if (activeEntity.type === 'brood') {
								Object.entries(sortedData).forEach(([ key, val, ]) => {
									if (val.name === 'brood_enable_transition' && !activeEntity[key]) {
										activeEntity[key] = {
											[val.type]: val.default_value,
										};
									}
								});
							}

							// Collect all explicitly categorized setting names
							const allCommonCategorized = Object.values(COMMON_SETTING_CATEGORIES)
								.flatMap((cat) => {
									return cat.settings;
								});
							const allExcludedFromComponent = [
								...allCommonCategorized,
								...PRIORITY_SETTINGS,
								...BOTTOM_SETTINGS,
								...MOCK_NOVA_SETTING_CATEGORY['Mock Nova'].settings,
							];

							// Build component-specific settings (everything not in common categories, priority, or bottom)
							const componentSettings = Object.entries(sortedData).filter(([ , val, ]) => {
								return !allExcludedFromComponent.includes(val.name) &&
									!hiddenSettings.includes(val.name);
							});

							// Sort custom_tlc_builder_name to the top of component settings
							componentSettings.sort((a, b) => {
								if (a[1].name === 'custom_tlc_builder_name') return -1;
								if (b[1].name === 'custom_tlc_builder_name') return 1;
								return sortWideSettingsToBottom(a, b);
							});

							const componentDisplayName = display_name || type || 'Component';

							return (
								<div className={style.compactSettingsContainer}>
									{/* Mock Nova (fake, non-schema settings) — own section at the very top.
									    Exclude every other setting name so renderCategorizedSettings'
									    catch-all "Component Settings" bucket doesn't duplicate them here. */}
									{this.renderCategorizedSettings(
										MOCK_NOVA_SETTING_CATEGORY,
										sortedData,
										hiddenSettings,
										componentType,
										Object.values(sortedData)
											.map((val) => { return val.name; })
											.filter((name) => { return !MOCK_NOVA_SETTING_CATEGORY['Mock Nova'].settings.includes(name); })
									)}

									{/* Priority settings at top (venue_id, is_visual_content_loop) */}
									{(() => {
										const priorityEntries = Object.entries(sortedData).filter(([ , val, ]) => {
											return PRIORITY_SETTINGS.includes(val.name) &&
												!hiddenSettings.includes(val.name);
										});
										if (priorityEntries.length === 0) return null;

										return (
											<div className={style.staticSettingsCategoryGrid} style={{
												marginBottom: '0.75em',
											}}>
												{priorityEntries.map(([ key, val, ]) => {
													return this.renderSetting(componentType, key, val);
												})}
											</div>
										);
									})()}

									{/* Dynamic component-specific category */}
									{componentSettings.length > 0 && (
										<div className={style.staticSettingsCategoryContainer}>
											<div className={style.staticSettingsCategoryHeader}>
												<CustomIcon icon="cog" className={style.staticSettingsCategoryIcon} />
												<span>{componentDisplayName} Component Settings</span>
											</div>
											<div className={style.staticSettingsCategoryGrid}>
												{componentSettings.map(([ key, val, ]) => {
													return this.renderSetting(componentType, key, val);
												})}
											</div>
										</div>
									)}

									{/* Common categories: Styling, Customer Portal, Order */}
									{this.renderCategorizedSettings(
										COMMON_SETTING_CATEGORIES,
										sortedData,
										hiddenSettings,
										componentType,
										[
											...componentSettings.map(([ , val, ]) => {
												return val.name;
											}),
											...PRIORITY_SETTINGS,
											...BOTTOM_SETTINGS,
										]
									)}

									{/* show_on_phase at bottom with wide class */}
									{(() => {
										const bottomEntries = Object.entries(sortedData).filter(([ , val, ]) => {
											return BOTTOM_SETTINGS.includes(val.name) &&
												!hiddenSettings.includes(val.name);
										});
										if (bottomEntries.length === 0) return null;

										return (
											<div className={style.bottomStandaloneSetting}>
												{bottomEntries.map(([ key, val, ]) => {
													return this.renderSetting(componentType, key, val);
												})}
											</div>
										);
									})()}
								</div>
							);
						})()
					)}
				</div>
			</div>
		);
	}

	buildListingCollectionCustomField() {
		const {
			activeEntityKey,
			flatData,
		} = this.state;

		const listingConfigMap = this.context?.listingConfigMap || {};

		const listingConfigMapEntries = Object.entries(listingConfigMap);
		let disabled = false;
		// If the account has no listings_config components associated to it, set disabled to true.
		if (!listingConfigMapEntries.length) {
			disabled = true;
		}

		let options = [];
		let listingsConfigValue = null;
		let listingCollectionEntityKey = '';
		let hasChild = false;

		if (!disabled) {
			// Find the Tier 1 listings_config components to build options array.
			listingConfigMapEntries.forEach(([ entityKey, m2Vals, ]) => {
				const listingConfigUUID = entityKey;

				if (m2Vals.root) {
					const listingConfigDisplayName = m2Vals.display_name ||
						getFDValue(this.props.fd, `listing_config.${listingConfigUUID}.display_name`);
					options.push({
						label: listingConfigDisplayName,
						value: listingConfigUUID,
					});
				}
			});

			// Find the component_to_listing_collection_map.
			for (const entityKey of Object.keys(flatData[activeEntityKey])) {
				if (entityKey.startsWith('component.')) {
					hasChild = true;
				}

				if (entityKey.startsWith('listing_collection.')) {
					const {
						m1ID: listingCollectionUUID,
					} = parseEntityName(entityKey);
					listingCollectionEntityKey = `listing_collection.${listingCollectionUUID}`;

					// Find the listing_collection_to_listing_config_map
					for (const [ m2EntityKey, m2Value, ] of Object.entries(flatData[listingCollectionEntityKey])) {
						// Skip pending deletions
						if (!m2Value) {
							continue;
						}
						if (m2EntityKey.startsWith('listing_config.')) {
							const {
								m1ID: listingConfigUUID,
							} = parseEntityName(m2EntityKey);

							// Set the value for react-select
							for (const option of options) {
								if (option.value === listingConfigUUID) {
									listingsConfigValue = option;
									break;
								}
							}

							break;
						}
					}
				}
			}
		}

		options.sort((a, b) => {
			const labelA = (a.label || '').toLowerCase();
			const labelB = (b.label || '').toLowerCase();

			if (labelA > labelB) {
				return 1;
			}

			if (labelA < labelB) {
				return -1;
			}

			return 0;
		});

		return (
			<div className={style.settingContainer} data-test-id="listingConfig">
				<div className={style.settingLabel} data-test-id="settingLabel">
					Listing Config
				</div>
				<Select
					isDisabled={disabled}
					options={options}
					styles={dropdownStyles}
					components={selectComponents}
					dropdownIndicatorTestId="indicator-listingConfig"
					optionTestIdPrefix="option"
					onChange={(e) => {
						if (hasChild) {
							this.updateListingConfigRelation(listingCollectionEntityKey, e.value, listingsConfigValue?.value);
						} else {
							this.props.app.openModal({
								modalSize:  1,
								showModal:  true,
								modalProps: {
									omitClose: true,
									title:     'Warning',
									jsx:       (
										<ListingCollectionWarning
										/>
									),
								},
							});
						}
					}}
					value={listingsConfigValue}
				/>
			</div>
		);
	}

	renderIcon(icon) {
		if (typeof icon === 'string') {
			if (icon.startsWith('http')) {
				return <img src={icon} alt="" className={style.iconImage} />;
			} else if (icon.startsWith('<')) {
				return <span dangerouslySetInnerHTML={{
					__html: icon,
				}} />;
			}
		}
		return icon;
	}

	buildDefaultListShownOptions(listingConfigUUID, listingConfigMap, childrenMap, accum, tier = 1) {
		const configData = listingConfigMap[listingConfigUUID];
		if (!configData) return accum;

		let displayText = '';

		if (tier > 1) {
			for (let tierOffset = 1; tierOffset < tier; tierOffset++) {
				displayText = '-' + displayText;
			}
			displayText += ' ';
		}

		displayText += configData.display_name;

		accum.push({
			label: displayText,
			value: `listing_config.${listingConfigUUID}`,
		});

		const children = childrenMap[listingConfigUUID] || [];
		for (const childUUID of children) {
			accum = this.buildDefaultListShownOptions(childUUID, listingConfigMap, childrenMap, accum, tier + 1);
		}

		return accum;
	}

	buildListingsScrollerCustomField() {
		const {
			activeEntityKey, flatData,
		} = this.state;
		const {
			settingMap,
		} = this.props;
		const listingConfigMap = this.context?.listingConfigMap || {};

		const listingConfigMapEntries = Object.entries(listingConfigMap);
		let disabled = false;
		// If the account has no listings_config components associated to it, set disabled to true.
		if (!listingConfigMapEntries.length) {
			disabled = true;
		}

		let options = [];
		let listingsConfigValue = null;
		const defaultListShownUUID = settingMap?.['default_list_shown']?.uuid;
		let scrollerDefault = defaultListShownUUID ?
			getFDValue(this.props.fd, `${activeEntityKey}.setting.${defaultListShownUUID}.string`) :
			null;

		if (defaultListShownUUID && flatData[activeEntityKey]?.[`setting.${defaultListShownUUID}`]) {
			scrollerDefault = flatData[activeEntityKey][`setting.${defaultListShownUUID}`].string;
		}

		if (scrollerDefault) {
			const scrollerDefaultUUID = scrollerDefault.replace('listing_config.', '');
			const listingConfigDisplayName = listingConfigMap[scrollerDefaultUUID]?.display_name ||
				getFDValue(this.props.fd, `${scrollerDefault}.display_name`);
			listingsConfigValue = {
				label: listingConfigDisplayName,
				value: scrollerDefault,
			};
		}

		if (!disabled) {
			// Build parent -> children lookup from listingConfigMap
			const childrenMap = {};
			for (const [ uuid, data, ] of listingConfigMapEntries) {
				if (data.parentUUID && !data.root) {
					if (!childrenMap[data.parentUUID]) {
						childrenMap[data.parentUUID] = [];
					}
					childrenMap[data.parentUUID].push(uuid);
				}
			}

			// Find the listing_config selected on the child listing_collection.
			// Only configs from that hierarchy should be shown as options.
			let selectedListingConfigUUID = null;
			for (const key of Object.keys(flatData[activeEntityKey])) {
				if (key.startsWith('component.') && flatData[key]?.type === 'listing_collection') {
					for (const childKey of Object.keys(flatData[key])) {
						if (childKey.startsWith('listing_collection.')) {
							for (const [ entityKey, entityVal, ] of Object.entries(flatData[childKey])) {
								if (entityKey.startsWith('listing_config.') && entityVal) {
									const { m1ID, } = parseEntityName(entityKey);
									selectedListingConfigUUID = m1ID;
									break;
								}
							}
							break;
						}
					}
					break;
				}
			}

			if (selectedListingConfigUUID) {
				options = this.buildDefaultListShownOptions(selectedListingConfigUUID, listingConfigMap, childrenMap, []);
			}
		}

		return (
			<div className={style.settingContainer} data-test-id="defaultListShown">
				<div className={style.settingLabel} data-test-id="settingLabel">
					Default List Shown
				</div>
				<Select
					isDisabled={disabled}
					options={options}
					styles={dropdownStyles}
					components={selectComponents}
					dropdownIndicatorTestId="indicator-defaultListShown"
					optionTestIdPrefix="option"
					onChange={(e) => {
						if (defaultListShownUUID) {
							this.updateSettingValue(`setting.${defaultListShownUUID}`, e.value, 'string');
						}
					}}
					value={listingsConfigValue}
				/>
			</div>
		);
	}

	async updateListingConfigRelation(listingCollectionEntityKey, newListingsConfigUUID, oldListingsConfigUUID) {
		if (newListingsConfigUUID === oldListingsConfigUUID) {
			return;
		}
		if (!listingCollectionEntityKey) {
			return;
		}

		const {
			app,
			accountUUID,
			fd,
			activeSystemFlatData,
			activeSystemUUID,
			activeTLCUUID,
			settingMap,
			pendingUpdates,
			systemProgramming,
		} = this.props;

		const {
			activeEntity,
			flatData,
		} = this.state;

		// First-time pick on a freshly-created listing_collection (no old config
		// to swap from). Just attach the new listing_config locally; the existing
		// save flow persists it. No cascade is needed because the collection has
		// no listing_groups, listings, or listing_column_groups yet.
		if (!oldListingsConfigUUID) {
			const data = JSON.parse(JSON.stringify(flatData));
			data[listingCollectionEntityKey][`listing_config.${newListingsConfigUUID}`] = {};
			this.setState({ flatData: data });
			return;
		}

		// Orphan check: a listing that lives only in this listing_collection via
		// listing_columns owned by the old listing_config would lose its only home.
		if (fd !== undefined && fd[`listing_config.${oldListingsConfigUUID}`]) {
			const listingColumns = {};
			Object.keys(fd[`listing_config.${oldListingsConfigUUID}`]).forEach((listingConfigKey) => {
				if (listingConfigKey.startsWith('listing_column.')) {
					listingColumns[listingConfigKey] = true;
				}
			});

			const listingsFromConfig = [];
			Object.entries(fd).forEach(([ key, value, ]) => {
				if (key.startsWith('listing.')) {
					Object.keys(value).forEach((key2) => {
						if (listingColumns[key2]) {
							listingsFromConfig.push(key);
						}
					});
				}
			});

			let blockDelete = false;
			listingsFromConfig.forEach((listing) => {
				Object.entries(fd).forEach(([ key, value, ]) => {
					if (key.startsWith('listing_collection.') && value[listing] && key === listingCollectionEntityKey) {
						blockDelete = true;
					}
				});
			});

			if (blockDelete) {
				window.alert(`Cannot complete this listing_config change.\nContinuing with this change will leave existing listings orphaned and they will disappear from view.\nPlease assign any listings that are only assigned to [${activeSystemFlatData?.display_name}] to another system before continuing.`);
				return;
			}
		}

		if (!window.confirm('Changing the listing config will detach all assigned listings and rebuild the listing groups for this listing collection. This cannot be undone. Continue?')) {
			return;
		}

		const listingCollectionUUID = listingCollectionEntityKey.split('.')[1];

		// Draft listing_collections (Step 1 template editing, or unsaved Step 4 additions)
		// live only in the local flatData / template_skeleton JSON — there is no DB row
		// for the swap endpoint to act on. Mutate flatData locally; the existing save
		// path persists the change.
		if (listingCollectionUUID.startsWith('_')) {
			const isTouch = !!flatData?.[activeTLCUUID]?.[`setting.${settingMap?.is_touch?.uuid}`]?.bool;
			let data = JSON.parse(JSON.stringify(flatData));

			data[listingCollectionEntityKey][`listing_config.${oldListingsConfigUUID}`] = false;
			data[listingCollectionEntityKey][`listing_config.${newListingsConfigUUID}`] = {};

			Object.keys(activeEntity).forEach((entityKey) => {
				if (flatData[entityKey] && flatData[entityKey].type === 'listing_group') {
					const listingGroupComponent = flatData[entityKey];
					Object.keys(listingGroupComponent).forEach((childKey) => {
						if (childKey.startsWith('listing_group.')) {
							const listingGroupEntity = flatData[childKey];

							Object.keys(listingGroupEntity).forEach((key) => {
								if (key.startsWith('listing_config.') && key !== `listing_config.${newListingsConfigUUID}`) {
									data[childKey][key] = false;
								}
							});

							let isParentConfig = true;
							let currentConfig = `listing_config.${newListingsConfigUUID}`;
							while (isParentConfig) {
								data[childKey][currentConfig] = {
									show_sub_nav_button: isTouch,
									mobile_display:      true,
								};
								isParentConfig = false;
								Object.keys(fd[currentConfig] || {}).forEach((key) => {
									if (key.startsWith('listing_config.')) {
										isParentConfig = true;
										currentConfig = key;
									}
								});
							}

							// Detach listing_column_group relations from the listing_group AND
							// remove the column_group entities themselves — they were structured
							// around the OLD listing_config's columns. Scope to listing_column_group
							// keys only; iterating all child keys (the pre-DEV-11671 behavior) would
							// call deleteEntityFromFlatData on listing_config keys, which removes
							// each listing_config from its true parent (the account), destroying
							// the account's listing_configs.
							Object.keys(listingGroupEntity).forEach((key) => {
								if (key.startsWith('listing_column_group.')) {
									data[childKey][key] = false;
									data = this.deleteEntityFromFlatData(key, data);
								}
							});
						}
					});
				}
			});

			this.setState({ flatData: data });
			return;
		}

		// Saved listing_collection (Step 4 against real DB row) — server-side swap.
		// Block when there are unsaved local changes: the swap is destructive
		// server-side, so any pending edits that reference this listing_collection
		// or its child listing_groups would orphan.
		if (pendingUpdates && Object.keys(pendingUpdates).length > 0) {
			window.alert('Please save or discard your pending changes before changing the listing config.');
			return;
		}

		const session = getSession();
		const isTouch = !!flatData?.[activeTLCUUID]?.[`setting.${settingMap?.is_touch?.uuid}`]?.bool;

		try {
			await axios.post(`${broncoURL}/listing_collections/swap_config`, {
				listing_collection_uuid: listingCollectionUUID,
				old_listing_config_uuid: oldListingsConfigUUID,
				new_listing_config_uuid: newListingsConfigUUID,
				is_touch:                isTouch,
			}, {
				headers: {
					'X-Auth-Session': session.session,
				},
			});
		} catch (err) {
			const msg = err?.response?.data || err?.message || 'unknown error';
			window.alert(`Failed to update listing config: ${msg}`);
			return;
		}

		if (systemProgramming && typeof systemProgramming.fetchAccountSystem === 'function' && activeSystemUUID) {
			systemProgramming.fetchAccountSystem(activeSystemUUID);
		} else if (app && typeof app.fetchAccountTemplates === 'function' && accountUUID) {
			app.fetchAccountTemplates(accountUUID);
		}
	}

	renderData() {
		const {
			activeEntityKey,
		} = this.state;

		if (!activeEntityKey) {
			return '';
		}

		if (this.state.showSettings) {
			return this.renderSettings();
		}

		return this.renderComponentTypes();
	}

	getChildEntities(entity, entityKey, parentTreeList = []) {
		const {
			app,
		} = this.props;

		const {
			flatData,
			rules,
		} = this.state;

		const {
			settingMap,
		} = app.state;

		if (entity.type === 'brood' && entityKey.startsWith('component._')) {
			let broodIsVisualContentLoopSettingUUID = settingMap?.['is_visual_content_loop']?.uuid;
			if (entity[`setting.${broodIsVisualContentLoopSettingUUID}`]?.bool === true) {
				return [];
			}
		}

		let childEntities = [];

		Object.entries(entity).forEach(([ childKey, childVal, ]) => {
			if (childKey.startsWith('component.')) {
				// Skip components that have a false relation.
				if (childVal === false) {
					return;
				}

				if (flatData[childKey]) {
					// Skip the child entity if it doesn't have a type.
					if (!flatData[childKey].type) {
						return;
					}

					childEntities.push({
						parentTreeList: [ ...parentTreeList, entityKey, ],
						accepted:       rules[flatData[childKey].type] || [],
						entity:         flatData[childKey],
						entityKey:      childKey,
					});
				}
			}

			if (childKey.startsWith('visual_content_loop')) {
				childEntities.push({
					isVisualContentLoopChild:	true,
					parentTreeList:           [], // [ ...parentTreeList, entityKey, ],
					accepted:                 [], // rules[childVal.type] || [],
					entity:                   childVal,
					entityKey:                childKey,
				});
			}
		});

		let tlcLayoutOrderSetting = settingMap?.['tlc_layout_order'];
		let tlcLayoutOrderSettingM1Key = tlcLayoutOrderSetting ? `setting.${tlcLayoutOrderSetting.uuid}` : null;

		let customTLCBuilderNameSetting = settingMap?.['custom_tlc_builder_name'];
		let customTLCBuilderNameSettingM1Key = customTLCBuilderNameSetting ? `setting.${customTLCBuilderNameSetting.uuid}` : null;

		// sort by one of:
		// - order
		// - custom label
		// - component type
		childEntities.sort((a, b) => {
			let aOrder = tlcLayoutOrderSettingM1Key ? (a.entity[tlcLayoutOrderSettingM1Key] || {})[tlcLayoutOrderSetting.type] : undefined;
			let bOrder = tlcLayoutOrderSettingM1Key ? (b.entity[tlcLayoutOrderSettingM1Key] || {})[tlcLayoutOrderSetting.type] : undefined;

			if (aOrder < bOrder) {
				return -1;
			}

			if (aOrder > bOrder) {
				return 1;
			}

			let aCustomName = customTLCBuilderNameSettingM1Key ? (a.entity[customTLCBuilderNameSettingM1Key] || {})[customTLCBuilderNameSetting.type] : undefined;
			let bCustomName = customTLCBuilderNameSettingM1Key ? (b.entity[customTLCBuilderNameSettingM1Key] || {})[customTLCBuilderNameSetting.type] : undefined;

			if (aCustomName && bCustomName) {
				if ((aCustomName < bCustomName)) {
					return -1;
				}

				if (aCustomName > bCustomName) {
					return 1;
				}
			}

			if (aCustomName && !bCustomName) {
				return -1;
			}

			if (bCustomName && !aCustomName) {
				return 1;
			}

			let aType = a.entity.type;
			let bType = b.entity.type;

			if (aType < bType) {
				return -1;
			}

			if (aType > bType) {
				return 1;
			}

			return 0;
		});

		return childEntities;
	}

	setActiveEntity(entity, entityKey) {
		this.setState({
			activeEntity:    entity,
			activeEntityKey: entityKey,
			showSettings:    true,	// Show settings when a new entity is selected
		});
	}

	updateDnDRelations(childKey, oldParentKey, newParentKey) {
		const {
			flatData,
		} = this.state;

		// Avoid pass by reference mutation!
		let data = JSON.parse(JSON.stringify(flatData));

		// Delete the child from the old parent.
		delete data[oldParentKey][childKey];
		// Add the child to the new parent.
		data[newParentKey][childKey] = {};
		// Update the parent key for the child.
		data[childKey].parent = newParentKey;

		this.setState({
			flatData: data,
		});
	}

	expandOrCollapseEntity(entityKey) {
		const {
			expandedOrCollapsedEntities,
			flatData,
		} = this.state;

		const {
			fd,
		} = this.props;

		const expandRecursive = (entityKey) => {
			if (flatData[entityKey]) {
				expandedOrCollapsedEntities[entityKey] = true;
				for (const [ m1Key, m1Data, ] of Object.entries(flatData[entityKey])) {
					if (m1Key.startsWith('component.')) {
						expandRecursive(m1Key);
					}
				}
			}
		};

		if (!expandedOrCollapsedEntities[entityKey]) {
			expandRecursive(entityKey);
		} else {
			expandedOrCollapsedEntities[entityKey] = false;
		}

		this.setState({
			expandedOrCollapsedEntities,
		});
	}

	renderTree() {
		const {
			settingMap,
		} = this.props;

		const {
			flatData,
			rules,
			// broodVisualContentLoopProviders,
		} = this.state;

		let accepted = [];
		let entity = {};
		let entityKey = '';

		// Find the TLC and start the tree.
		Object.entries(flatData).forEach(([ key, val, ]) => {
			if (val.type === 'top_level_condor') {
				accepted = rules[val.type];
				entity = val;
				entityKey = key;
			}
		});

		return (
			<TreeEntity
				app={this.props.app}
				accountUUID={this.props.accountUUID}
				accepted={accepted}
				stateFlatData={flatData}
				activeEntityKey={this.state.activeEntityKey}
				activeSystemUUID={this.props.activeSystemUUID}
				componentTypeToSettingMap={this.state.componentTypeToSettingMap}
				entity={entity}
				entityKey={entityKey}
				fd={this.props.fd}
				getChildEntities={this.getChildEntities.bind(this)}
				expandOrCollapseEntity={this.expandOrCollapseEntity.bind(this)}
				expandedOrCollapsedEntities={this.state.expandedOrCollapsedEntities}
				level={0}
				setActiveEntity={this.setActiveEntity.bind(this)}
				updateDnDRelations={this.updateDnDRelations.bind(this)}
				templateSkeleton={this}
				settingMap={settingMap}
				broodVisualContentLoopProviders={this.state.broodVisualContentLoopProviders}
				systemProgramming={this.props.systemProgramming}
			/>
		);
	}

	renderSkeleton() {
		const {
			activeEntityKey,
			activeEntity,
			isCustom,
			rules,
			flatData,
		} = this.state;

		const {
			fd,
			activeTLCUUID,
		} = this.props;

		let addBtnClassName = 'button button-disabled';
		let cloneBtnClassName = 'button button-disabled';
		let deleteBtnClassName = 'button button-disabled';
		let expandOrCollapseBtnClassName = 'button button-disabled';
		let addBtnDisabled = true;
		let cloneBtnDisabled = true;
		let deleteBtnDisabled = true;
		let expandOrCollapseBtnDisabled = true;

		// If the activeEntity has a rule, enable the add button.
		if (activeEntity) {
			const {
				type,
			} = activeEntity;
			if (rules[type]) {
				addBtnClassName = 'button';
				addBtnDisabled = false;
			}

			if (activeEntity.type && activeEntity.type !== 'top_level_condor') {
				cloneBtnClassName = 'button';
				deleteBtnClassName = 'button';

				cloneBtnDisabled = false;
				deleteBtnDisabled = false;
			}

			expandOrCollapseBtnDisabled = false;
			expandOrCollapseBtnClassName = 'button';

			if (activeEntity.type === 'brood') {
				const isVisualContentLoopUUID = this.props.settingMap?.['is_visual_content_loop']?.uuid;
				const isVisualContentLoopEnabled = isVisualContentLoopUUID ?
					activeEntity[`setting.${isVisualContentLoopUUID}`]?.bool :
					false;
				if (isVisualContentLoopEnabled) {
					addBtnClassName = 'button button-disabled';
					cloneBtnClassName = 'button button-disabled';
					deleteBtnClassName = 'button button-disabled';
					expandOrCollapseBtnClassName = 'button button-disabled';

					addBtnDisabled = true;
					cloneBtnDisabled = true;
					deleteBtnDisabled = true;
					expandOrCollapseBtnDisabled = true;
				}
			}

			if (activeEntityKey.startsWith('visual_content_loop_')) {
				addBtnClassName = 'button button-disabled';
				cloneBtnClassName = 'button button-disabled';
				deleteBtnClassName = 'button button-disabled';
				expandOrCollapseBtnClassName = 'button button-disabled';

				addBtnDisabled = true;
				cloneBtnDisabled = true;
				deleteBtnDisabled = true;
				expandOrCollapseBtnDisabled = true;
			}
		}

		let isCustomElm = null;
		if (isCustom) {
			isCustomElm = (
				<span className={style.headerCustomLabel}>CUSTOM</span>
			);
		}

		let confirmModal = '';
		if (this.state.confirmModal === true) {
			confirmModal =
				<div className='confirm-modal-container'>
					<div className='confirm-modal-content'>
						{this.state.confirmModalContent}
					</div>
				</div>;
		}
		return (
			<React.Fragment>
				{confirmModal}
				<div className={style.mainContainer}>
					<div className={style.skeletonContainer}>
						<div className={style.skeletonContainerHeader}>
							{this.props.step === 1 && (
								<React.Fragment>
									<div className={style.orientationButtonsWrapper}>
										<span className={style.orientationLabel}>Slideshow Display:</span>
										<div className={style.orientationButtons}>
											<button
												className={this.props.orientation === 'HZ' ? 'button button-primary' : 'button'}
												onClick={() => {
													this.props.setOrientation('HZ');
												}}
											>
												HZ
											</button>
											<button
												className={this.props.orientation === 'VT' ? 'button button-primary' : 'button'}
												onClick={() => {
													this.props.setOrientation('VT');
												}}
											>
												VT
											</button>
										</div>
									</div>
								</React.Fragment>
							)}
							{this.props.step === 2 && (
								<React.Fragment>
									<div
										className={style.condorPreviewIcon}
										onClick={(e) => {
											e.stopPropagation();
											this.props.handlePreview();
										}}
									>
										<CustomIcon icon="eye" color="#4BA046" />
									</div>
									<div
										className={style.condorPreviewIcon}
										onClick={(e) => {
											e.stopPropagation();
											this.props.handlePreview(true);
										}}
									>
										<CustomIcon icon="mobile-alt" color="#4BA046" />
									</div>
								</React.Fragment>
							)}
							<div className={style.headerBtnContainer}>
								<button
									className={addBtnClassName}
									onClick={(e) => {
										this.setState({
											showSettings: false,
										});
									}}
									disabled={addBtnDisabled}
									alt='Add'
									data-ts-id='addComponentButton'
								>
									<CustomIcon icon='plus' />
								</button>
								<button
									className={cloneBtnClassName}
									onClick={(e) => {
										e.preventDefault();
										this.cloneEntity();
									}}
									disabled={cloneBtnDisabled}
									alt='Clone'
									data-ts-id='cloneComponentButton'
								>
									<CustomIcon icon='copy' />
								</button>
								<button
									className={deleteBtnClassName}
									onClick={(e) => {
										e.preventDefault();
										this.deleteEntity();
									}}
									disabled={deleteBtnDisabled}
									alt='Delete'
									data-ts-id='deleteComponentButton'
								>
									<CustomIcon icon='trash' />
								</button>
								{this.props.step === 4 && (
									<button
										className='button'
										onClick={(e) => {
											e.preventDefault();
											this.saveAsTemplate();
										}}
										alt='Save as Template'
										data-ts-id='saveAsTemplateButton'
									>
										<CustomIcon icon='save' />
									</button>
								)}
								<button
									className={expandOrCollapseBtnClassName}
									onClick={(e) => {
										const {
											expandedOrCollapsedEntities,
										} = this.state;
										e.preventDefault();

										let tlcUUID = activeTLCUUID;
										let flatDataSource = flatData ? flatData : fd;

										const collapseRecursive = (componentUUID) => {
											if (flatDataSource[componentUUID]) {
												expandedOrCollapsedEntities[componentUUID] = true;
												for (const [ m1Key, m1Data, ] of Object.entries(flatDataSource[componentUUID])) {
													if (m1Key.startsWith('component.')) {
														collapseRecursive(m1Key);
													}
												}
											}
										};

										if (!tlcUUID) {
											flatDataSource = this.props.existingFlatData;

											for (const [ m1Key, m1Data, ] of Object.entries(flatDataSource)) {
												if (m1Data.type === 'top_level_condor') {
													tlcUUID = m1Key;
													break;
												}
											}
										}

										collapseRecursive(tlcUUID);

										this.setState({
											expandedOrCollapsedEntities,
										});
									}}
									disabled={expandOrCollapseBtnDisabled}
									alt='Expand All'
									data-ts-id='expandAllButton'
								>
									<div className={style.expandCollapseAllIcon} >
										<CustomIcon icon='angle-down' />
									</div>
								</button>
								<button
									className={expandOrCollapseBtnClassName}
									onClick={(e) => {
										e.preventDefault();
										this.setState({
											expandedOrCollapsedEntities: {},
										});
									}}
									disabled={expandOrCollapseBtnDisabled}
									alt='Collapse All'
									data-ts-id='collapseAllButton'
								>
									<div className={style.expandCollapseAllIcon} >
										<CustomIcon icon='angle-right' />
									</div>
								</button>
							</div>
						</div>
						<div className={style.skeletonScroller}>
							{this.renderTree()}
						</div>
					</div>
					{this.renderData()}
				</div>
			</React.Fragment>
		);
	}

	render() {
		if (Object.keys(this.state.componentTypeToSettingMap).length === 0) {
			return '';
		}

		if (!this.props.settingMap || !Object.keys(this.props.settingMap).length) {
			return '';
		}

		return (
			<React.Fragment>
				{this.renderSkeleton()}
			</React.Fragment>
		);
	}

	componentWillUnmount() {
		this._isMounted = false;

		if (this._apiAbortController) {
			this._apiAbortController.abort();
			this._apiAbortController = null;
		}

		if (this._s3ListReq && typeof this._s3ListReq.abort === 'function') {
			this._s3ListReq.abort(); this._s3ListReq = null;
		}
		if (this._s3HeadReq && typeof this._s3HeadReq.abort === 'function') {
			this._s3HeadReq.abort(); this._s3HeadReq = null;
		}

		if (this.someTimeout) {
			clearTimeout(this.someTimeout); this.someTimeout = null;
		}
		if (this.someInterval) {
			clearInterval(this.someInterval); this.someInterval = null;
		}

	}
}

function TreeEntity(props) {
	let {
		app,
		accountUUID,
		accepted,
		activeSystemUUID,
		activeEntityKey,
		entity,
		entityKey,
		fd,
		getChildEntities,
		level,
		parentTreeList = [],
		setActiveEntity,
		templateSkeleton,
		expandOrCollapseEntity,
		expandedOrCollapsedEntities,
		stateFlatData,
		settingMap,
		broodVisualContentLoopProviders,
	} = props;

	const ref = useRef(null);

	const [{
		isDragging,
	}, drag, preview, ] = useDrag({
		item: {
			entity,
			entityKey,
		},

		type: entity.type,

		collect: (monitor) => {
			return {
				isDragging: !!monitor.isDragging(),
			};
		},
	});

	const [{
		isOver, isOverCurrent, canDrop,
	}, drop, ] = useDrop({
		accept: accepted,

		collect: (monitor) => {
			return {
				isOver:        !!monitor.isOver(),
				isOverCurrent: !!monitor.isOver({
					shallow: true,
				}),
				canDrop: monitor.canDrop(),
			};
		},

		drop: (item) => {

			// Don't drop an entity into itself
			if (item.entityKey === entityKey) {
				return;
			}

			// Don't drop an entity lower into its own tree.
			if (~parentTreeList.indexOf(item.entityKey)) {
				return;
			}

			let oldParentKey = item.entity.parent;
			let newParentKey = entityKey;
			props.updateDnDRelations(item.entityKey, oldParentKey, newParentKey);
		},
	});

	// TODO: A better way?
	let componentTypesToIgnore = [
		'listing',
		'shared_ui_table_config',
	];

	// Don't render certain component_types.
	if (~componentTypesToIgnore.indexOf(entity.type)) {
		return '';
	}

	preview(drop(ref));

	let selected = '';
	let canDropComponent = '';

	// If activeEntityKey matches the entityKey, it's been clicked by the user. Make it lightblue.
	if (activeEntityKey === entityKey) {
		selected = style.selected;
	}

	// If an entity is currently hovering and can be dropped, change the background color to lightgreen.
	if (isOverCurrent && isOver && canDrop) {
		canDropComponent = style.canDropComponent;
	}

	// Set the opacity to 0 if we're dragging an entity.
	const opacity = isDragging ? 0 : 1;

	let dragHandle = (
		<div className={style.dragHandle} ref={drag}>
			<CustomIcon icon='bars' />
		</div>
	);

	const blockedEntityTypesForDrag = [
		'top_level_condor',
		'listing_group',
	];

	if (~blockedEntityTypesForDrag.indexOf(entity.type) || (props.isTemplateEditingMode && entity.is_visual_content_loop_provider)) {
		dragHandle = null;
	}

	const settingNameToInfo = props.app.state.settingMap || {};

	let displayName = getComponentDisplayName(entity.type);
	const customTLCBuilderNameUUID = settingNameToInfo['custom_tlc_builder_name']?.uuid;

	if ((entity[`setting.${customTLCBuilderNameUUID}`]?.string || '').trim() !== '') {
		displayName = entity[`setting.${customTLCBuilderNameUUID}`].string;
	}

	if (entity.is_visual_content_loop_provider) {
		const systemSpecificSettings = (entity.settings || {})[activeSystemUUID] || {};
		displayName = systemSpecificSettings.custom_tlc_builder_name || '';

		if (displayName.trim() !== '') {
			displayName = `${getComponentDisplayName(entity.type)}: ${displayName}`;
		} else {
			displayName = getComponentDisplayName(entity.type);
		}

		if (props.isTemplateEditingMode) {
			displayName = `🔒 ${displayName}`;
		}
	}

	if (entity.category) {
		displayName = `${getComponentDisplayName(entity.category)}: ${displayName}`;
	}

	let hasChildComponents = false;

	if (stateFlatData[entityKey]) {
		for (const m1Key of Object.keys(stateFlatData[entityKey])) {
			if (m1Key.startsWith('component.') && stateFlatData[entityKey].type !== 'pelican') {
				hasChildComponents = true;
				break;
			}
		}
	}

	let isTopLevelComponent = stateFlatData[entityKey]?.description === 'top_level_condor';
	let showCaret = hasChildComponents && !isTopLevelComponent;

	let broodVisualContentLoopEnabled = false;
	let broodVisualContentLoopID = 0;

	if (stateFlatData[entityKey]?.type === 'brood' && entityKey.startsWith('component._')) {
		hasChildComponents = false;
		showCaret = false;
	}

	if (stateFlatData[entityKey]?.type === 'brood' && !entityKey.startsWith('component._')) {
		let broodIsVisualContentLoopSettingUUID = settingMap?.['is_visual_content_loop']?.uuid;
		if (stateFlatData[entityKey][`setting.${broodIsVisualContentLoopSettingUUID}`]?.bool === true) {
			broodVisualContentLoopEnabled = true;

			let broodVisualContentLoopIDSettingUUID = settingMap?.['visual_content_loop_id']?.uuid;
			broodVisualContentLoopID = stateFlatData[entityKey][`setting.${broodVisualContentLoopIDSettingUUID}`]?.int || 0;

			hasChildComponents = true;
			showCaret = false;

			// remove existing components
			for (const m1Key of Object.keys(stateFlatData[entityKey])) {
				if (m1Key.startsWith('component.')) {
					stateFlatData[m1Key].componentDisabled = true;
				}

				if (m1Key.startsWith('visual_content_loop_')) {
					Reflect.deleteProperty(stateFlatData[entityKey], m1Key);
				}
			}

			// populate with providers from the linked VCL if a system exists
			// AND they have been fetched after the most recent save has completed
			if (broodVisualContentLoopID && broodVisualContentLoopProviders[broodVisualContentLoopID] && activeSystemUUID) {
				broodVisualContentLoopProviders[broodVisualContentLoopID].forEach((provider) => {
					if (provider.lastFetchedTime > props.systemProgramming?.state?.lastSaveTime) {
						let providerComponentKey = `visual_content_loop_${broodVisualContentLoopID}::${provider.type}::${provider.id}::${activeSystemUUID}`;
						stateFlatData[entityKey][providerComponentKey] = {
							...provider,
						};
					}
				});
			}
		} else {
			hasChildComponents = true;
			showCaret = true;

			// bring back the components if they exist
			for (const m1Key of Object.keys(stateFlatData[entityKey])) {
				if (m1Key.startsWith('component.')) {
					stateFlatData[m1Key].componentDisabled = false;
				}

				if (m1Key.startsWith('visual_content_loop_')) {
					Reflect.deleteProperty(stateFlatData[entityKey], m1Key);
				}
			}
		}
	}

	let canDropIcon = <div></div>;
	if (accepted && Array.isArray(accepted) && accepted.length > 0) {
		canDropIcon = (
			<div className={style.iconsContainer} >
				{showCaret > 0 &&
				<div
					className={`${style.angleContainer} ${!expandedOrCollapsedEntities[entityKey] ? style.rotatedIcon : ''}`}
					onClick={(e) => {
						e.stopPropagation();
						if (level === 0) {
							return;
						}
						expandOrCollapseEntity(entityKey);
					}}
					data-ts-id={`expandCollapseButton-${displayName.replace(/\s+/g, '')}`}
				>
					<CustomIcon icon='angle-down' />
				</div>}

				<CustomIcon icon='object-group' color='#EFAF00' />
			</div>
		);
	}

	let parentIdentifier = props.parentIdentifier || '';
	let index = props.index || 0;
	let currentIdentifier = `${parentIdentifier ? `${parentIdentifier}.` : ''}${index}`;

	return (
		<React.Fragment>
			<div
				className={`${style.treeEntity} ${selected} ${canDropComponent}`}
				onClick={(e) => {
					e.preventDefault();

					if (entity.type === 'brood' && broodVisualContentLoopEnabled) {
						if (entityKey.startsWith('component._')) {
							return setActiveEntity(entity, entityKey);
						}

						templateSkeleton.fetchBroodVisualContentLoopProviders(broodVisualContentLoopID, () => {
							templateSkeleton.setActiveEntity(entity, entityKey);
						});

						return;
					}

					setActiveEntity(entity, entityKey);
				}}
				ref={ref}
				style={{
					opacity,
					position: 'relative',
				}}
			>
				{entity.is_grouped && (
					<div
						style={{
							position:        'absolute',
							left:            0,
							top:             0,
							bottom:          0,
							width:           '3px',
							backgroundColor: GROUPED_COMPONENT_COLOR,
							borderRadius:    '2px',
						}}
						title="Grouped component (shared across systems)"
					/>
				)}
				<div
					className={style.treeEntityInner}
					data-test-id="treeEntityInner"
					data-entity-id={`${displayName}-${currentIdentifier}`}
					style={{
						marginLeft: `${(level > 0 ? level - 1 : level) * 1.2 + (!showCaret && 1.8) - (level < 2 && !showCaret ? 0.3 : 0)}em`,
					}}
				>
					{canDropIcon}
					<div>
						{displayName}
						<br />
						{selected &&
							<Clipboard
								app={app}
								accountUUID={accountUUID}
								entity={entity}
								templateSkeletonM1Key={activeEntityKey}
								templateSkeleton={templateSkeleton}
							/>
						}
					</div>
				</div>
				{dragHandle}
			</div>

			{/* {broodVisualContentLoopEnabled &&
				<div className={style.broodVisualContentLoopEnabled}>
					Connected to Visual Content Loop ID: {broodVisualContentLoopID}
					<pre>
						{JSON.stringify(broodVisualContentLoopProviders[broodVisualContentLoopID] || [], null, 2)}
					</pre>
				</div>
			} */}

			{/* Recursively render the tree! */}
			{getChildEntities(entity, entityKey, parentTreeList)
				.filter((childEntity) => {
					if (childEntity.entity.type === 'pelican_slide') {
						return false;
					}

					if (accepted && level !== 0 && !expandedOrCollapsedEntities[entityKey]) {
						return false;
					}

					if (stateFlatData[childEntity.entityKey]?.componentDisabled || false) {
						return false;
					}

					return true;
				})
				.map((childEntity, i) => {
					return (
						<TreeEntity
							{...props}
							app={props.app}
							accountUUID={props.accountUUID}
							accepted={childEntity.accepted}
							entity={childEntity.entity}
							entityKey={childEntity.entityKey}
							activeSystemUUID={props.activeSystemUUID}
							key={childEntity.entityKey}
							level={level + 1}
							parentTreeList={childEntity.parentTreeList}
							stateFlatData={stateFlatData}
							index={i}
							parentIdentifier={currentIdentifier}
							settingMap={settingMap}
							broodVisualContentLoopProviders={broodVisualContentLoopProviders}
							systemProgramming={props.systemProgramming}
						/>
					);
				})}
		</React.Fragment>
	);
}

class JSONField extends React.Component {
	componentDidMount() {
		const {
			settingKey,
			type,
			defaultValue,
			updateSettingValue,
			value,
		} = this.props;

		if (!value && defaultValue) {
			updateSettingValue(settingKey, defaultValue, type);
		}
		let changed = false;
		const options = {
			mode:  'code',
			modes: [
				'code',
				'view',
				'form',
			],
			onValidate: () => {
				if (!changed) {
					return;
				}

				let json = JSON.stringify(this.jsoneditor.get());
				if (defaultValue === '[]') {
					(/^\[.*\]$/).test(json) && updateSettingValue(settingKey, json, type);
					return;
				}
				updateSettingValue(settingKey, json, type);
			},

			onChange: () => {
				changed = true;
				// Set value to null if a user highlights and deletes everything.
				if (this.jsoneditor) {
					let text = this.jsoneditor.getText();
					if (text === '[]') {
						updateSettingValue(settingKey, defaultValue || null, type);
					}
				}
			},
		};

		let parsedValue = null;
		try {
			parsedValue = JSON.parse(value);
		} catch (err) {

		}

		this.jsoneditor = new JSONEditor(this.container, options);

		// Display a blank value in the editor.
		if (parsedValue === null) {
			this.jsoneditor.setText('');
			return;
		}

		this.jsoneditor.set(parsedValue);
	}

	componentWillUnmount() {
		if (this.jsoneditor) {
			this.jsoneditor.destroy();
		}
	}

	render() {
		return (
			<div
				className='jsoneditor-react-container'
				ref={(elm) => {
					this.container = elm;
				}}
			/>
		);
	}
}
class ListingGroupCustomField extends React.Component {
	static contextType = ListingConfigContext;

	constructor(props) {
		super(props);

		this.state = {
			listingColumnOptionsMap:              {},
			listingConfigList:                    [],
			listingConfigToListingColumnGroupMap: {},
			expandedTiers:                        {},
		};

		this._prevListingConfigMap = null;
	}

	componentDidMount() {
		this._prevListingConfigMap = this.context?.listingConfigMap;
		this.buildListingConfigList();
	}

	componentDidUpdate(prevProps, prevState) {
		const ctxMap = this.context?.listingConfigMap;
		if (JSON.stringify(prevProps.activeEntity) !== JSON.stringify(this.props.activeEntity)
			|| ctxMap !== this._prevListingConfigMap) {
			this._prevListingConfigMap = ctxMap;
			this.buildListingConfigList();
		} else if (this.getColumnGroupSignature(prevProps) !== this.getColumnGroupSignature(this.props)) {
			// activeEntity is the listing_group *component*, which doesn't change when the
			// listing_group's column groups do — e.g. the post-save refetch swaps a new
			// column group's temp `_<uuid>` key for its real UUID. Without this rebuild the
			// map keeps the temp key and the new column isn't rendered until a refresh. (DEV-12185)
			this.buildListingConfigToListingColumnGroupMap(this.state.listingConfigList);
		}

		// Initialize expandedTiers when listingConfigToListingColumnGroupMap is first populated or its tier set changes.
		const prevKeysList = Object.keys(prevState.listingConfigToListingColumnGroupMap || {}).sort();
		const currentKeysList = Object.keys(this.state.listingConfigToListingColumnGroupMap || {}).sort();
		if (prevKeysList.join(',') !== currentKeysList.join(',') && currentKeysList.length > 0) {
			const expandedTiers = {};
			Object.keys(this.state.listingConfigToListingColumnGroupMap).forEach((tierKey, index) => {
				// Preserve existing state if known; otherwise default first tier expanded, others collapsed.
				expandedTiers[tierKey] = typeof this.state.expandedTiers[tierKey] === 'boolean' ?
					this.state.expandedTiers[tierKey] :
					index === 0;
			});
			this.setState({
				expandedTiers,
			});
		}

		this.correctColumnGroupOrders();
	}

	// Identifies the listing_group's live column groups and the tier (listing_config) each
	// belongs to — the inputs buildListingConfigToListingColumnGroupMap groups on. Order is
	// left out on purpose: correctColumnGroupOrders rewrites it in place on every update.
	getColumnGroupSignature({ flatData, listingGroupEntityKey, }) {
		const listingGroup = flatData?.[listingGroupEntityKey] || {};

		return Object.keys(listingGroup)
			.filter((key) => key.startsWith('listing_column_group.') && listingGroup[key])
			.map((key) => {
				const tiers = Object.keys(flatData[key] || {}).filter((childKey) => childKey.startsWith('listing_config.'));
				return `${key}:${tiers.sort().join(',')}`;
			})
			.sort()
			.join('|');
	}

	toggleTierExpanded(listingConfigKey) {
		this.setState({
			expandedTiers: {
				...this.state.expandedTiers,
				[listingConfigKey]: !this.state.expandedTiers[listingConfigKey],
			},
		});
	}

	correctColumnGroupOrders() {
		const {
			flatData, listingGroupEntityKey, stateCallback,
		} = this.props;

		const {
			listingConfigToListingColumnGroupMap,
		} = this.state;

		if (!flatData[listingGroupEntityKey] || !listingConfigToListingColumnGroupMap) {
			return;
		}

		let needsUpdate = false;

		Object.values(listingConfigToListingColumnGroupMap).forEach((listingColumnGroupList) => {
			listingColumnGroupList.forEach((listingColumnGroupKey, i) => {
				if (flatData[listingGroupEntityKey][listingColumnGroupKey] &&
					flatData[listingGroupEntityKey][listingColumnGroupKey].order !== i + 1) {
					flatData[listingGroupEntityKey][listingColumnGroupKey].order = i + 1;
					needsUpdate = true;
				}
			});
		});

		if (needsUpdate) {
			stateCallback('flatData', flatData);
		}
	}

	buildListingConfigToListingColumnGroupMap(listingConfigList) {
		const {
			flatData, listingGroupEntityKey,
		} = this.props;
		let listingConfigToListingColumnGroupMap = {};

		// Create keys for each avaiable listing_config
		listingConfigList.forEach((listingConfig) => {
			listingConfigToListingColumnGroupMap[`listing_config.${listingConfig.uuid}`] = [];
		});

		// Find the listing_column_group entities and sort by tier
		Object.entries(flatData[listingGroupEntityKey] || []).forEach(([ listingColumnGroupKey, listingColumnGroupVal, ]) => {
			if (listingColumnGroupKey.startsWith('listing_column_group.') && listingColumnGroupVal) {
				const listingColumnGroupData = flatData[listingColumnGroupKey];
				if (!listingColumnGroupData) return;
				for (const childKey of Object.keys(listingColumnGroupData)) {
					if (childKey.startsWith('listing_config.') && listingConfigToListingColumnGroupMap[childKey]) {
						listingConfigToListingColumnGroupMap[childKey] = [
							...listingConfigToListingColumnGroupMap[childKey],
							{
								entityKey: listingColumnGroupKey,
								order:     listingColumnGroupVal.order,
							},
						];
					}
				}
			}
		});

		// Sort column_groups by order and then filter down to only the entityKey.
		Object.values(listingConfigToListingColumnGroupMap).forEach((listingColumnGroupList) => {
			listingColumnGroupList.sort((a, b) => {
				if (a.order > b.order) {
					return 1;
				}
				if (a.order < b.order) {
					return -1;
				}
				return 0;
			}).filter((obj, i, arr) => {
				arr[i] = obj.entityKey;
				return true;
			});
		});

		this.setState({
			listingConfigToListingColumnGroupMap,
		});
	}

	buildListingConfigList() {
		const {
			activeEntity, flatData,
		} = this.props;

		let listingColumnOptionsMap = {};
		let listingConfigList = [];

		// Find the parent component that owns the listing_collection
		const listingCollectionComponent = flatData[activeEntity.parent] || {};

		// Find the listing_collection entity
		for (const entityKey of Object.keys(listingCollectionComponent)) {
			if (entityKey.startsWith('listing_collection')) {
				// Find the listing_config owned by the listing_collection
				for (const childKey of Object.keys(flatData[entityKey])) {
					if (childKey.startsWith('listing_config.')) {
						// If the listing_collection_to_listing_collection isn't pending deletion, create the listingConfigList list.
						if (flatData[entityKey][childKey]) {
							// Build the listingColumnOptionsMap and listingConfigList
							const obj = this.loopListingConfigTree(childKey);
							listingColumnOptionsMap = obj.listingColumnOptionsMap;
							listingConfigList = obj.listingConfigList;
						}
					}
				}
			}
		}

		this.buildListingConfigToListingColumnGroupMap(listingConfigList);

		this.setState({
			listingColumnOptionsMap,
			listingConfigList,
		});
	}

	loopListingConfigTree(listingConfigKey, listingConfigList = [], listingColumnOptionsMap = {}, tier = 0) {
		const listingConfigMap = this.context?.listingConfigMap || {};

		tier++;
		const {
			m1ID: listingConfigUUID,
		} = parseEntityName(listingConfigKey);

		if (!listingConfigMap[listingConfigUUID]) {
			console.warn('[ListingGroup] listingConfigMap missing UUID:', listingConfigUUID, 'Available keys:', Object.keys(listingConfigMap));
			return {
				listingColumnOptionsMap,
				listingConfigList,
			};
		}

		listingConfigList.push({
			tier,
			display_name: listingConfigMap[listingConfigUUID].display_name,
			uuid:         listingConfigUUID,
			offset:       0,
		});

		let displayName = listingConfigMap[listingConfigUUID].display_name;
		// Use the UUID from listingConfigMap (the CLONED data) instead of the recursion parameter
		const currentConfigKey = `listing_config.${listingConfigUUID}`;

		// Loop over children to see if there are child tiers and recursively build listingConfigList if so.
		Object.entries(listingConfigMap[listingConfigUUID]).forEach(([ entityKey, entityVal, ]) => {
			const {
				m1: childM1, m1ID: childM1UUID,
			} = parseEntityName(entityKey);

			if (childM1 === 'listing_column' && entityVal?.display_name) {
				listingColumnOptionsMap[currentConfigKey] = [
					...listingColumnOptionsMap[currentConfigKey] || [],
					{
						label: entityVal.display_name,
						value: {
							tier,
							uuid: childM1UUID,
						},
						displayName,
					},
				];
			}
			if (entityKey === 'childTiers' && entityVal.length) {
				const nestedChild = `listing_config.${entityVal[0]}`;
				this.loopListingConfigTree(nestedChild, listingConfigList, listingColumnOptionsMap, tier);
			}
		});

		return {
			listingColumnOptionsMap,
			listingConfigList,
		};
	}

	toggleMobileDisplay(listing) {
		listing.mobile_display = !listing.mobile_display;
	}

	toggleMobileWayfinding(listing) {
		listing.mobile_wayfinding = !listing.mobile_wayfinding;
	}

	toggleMobileDialing(listing) {
		listing.mobile_dialing = !listing.mobile_dialing;
	}

	toggleShowSubNav(listing) {
		listing.show_sub_nav_button = !listing.show_sub_nav_button;
	}

	buildNestedColumnOptions(listingColumnOptionsMap, currentConfigKey) {
		const { listingConfigMap, } = this.context;
		let optionShowList = [];

		let isRoot = false;
		let parentUUID = currentConfigKey;
		while (!isRoot) {
			if (!listingConfigMap[parentUUID]) {
				console.warn('[ListingGroup] buildNestedColumnOptions: broke parent chain at', parentUUID, '| optionShowList so far:', optionShowList);
				break;
			}

			if (listingConfigMap[parentUUID]['root']) {
				optionShowList.push(parentUUID);
				isRoot = true;
				break;
			}

			optionShowList.push(parentUUID);
			parentUUID = listingConfigMap[parentUUID]['parentUUID'];
		}

		let optionsList = [];
		Object.entries(listingColumnOptionsMap).forEach(([ listingConfigKey, listingConfigVal, ]) => {
			let listingConfigUUID = listingConfigKey.replace('listing_config.', '');
			if (optionShowList.includes(listingConfigUUID)) {
				let labelOptions = {};
				labelOptions['label'] = listingConfigVal[0]['displayName'];
				labelOptions['options'] = [];

				listingConfigVal.forEach((option) => {
					let tierOption = {
						label: option.label.split('\n')[0],
						value: option.value,
					};
					labelOptions['options'].push(tierOption);
				});

				optionsList.push(labelOptions);
			}

		});
		return optionsList;
	}

	addListingColumnGroup(listingConfigUUID) {
		if (!listingConfigUUID) {
			return;
		}

		const {
			listingConfigToListingColumnGroupMap,
		} = this.state;

		const {
			flatData,
			listingGroupEntityKey,
			stateCallback,
		} = this.props;
		const newListingColumnGroupUUID = `_${uuidv4()}`;

		if (!flatData[listingGroupEntityKey][`listing_config.${listingConfigUUID}`]) {
			flatData[listingGroupEntityKey][`listing_config.${listingConfigUUID}`] = {
				show_sub_nav_button: false,
				mobile_display:      true,
			};
		}

		const order = listingConfigToListingColumnGroupMap[`listing_config.${listingConfigUUID}`].length + 1;

		// Add listing_group_to_listing_column_group relation.
		flatData[listingGroupEntityKey][`listing_column_group.${newListingColumnGroupUUID}`] = {
			order,
		};

		// Add the listing_column_group to the flatData.
		flatData[`listing_column_group.${newListingColumnGroupUUID}`] = {
			parent: listingGroupEntityKey,

			// Map the listing_column_group to the listing_config that was selected.
			[`listing_config.${listingConfigUUID}`]: {},
		};

		// Add temp listing_column_group_to_listing_column relation.
		// listing_column.uuid needs to line up with m2UUID of listing_config.*.listing_column.*
		// Not adding UUID so it is ignored in /w/edit if the user never selects a listing_column for the listing_group.
		// NOTE: If a user saves a column_group without selecting a column in Step 5, the column_group needs to be deleted with this approach.
		flatData[`listing_column_group.${newListingColumnGroupUUID}`][`listing_column.`] = {};

		stateCallback('flatData', flatData);
		this.buildListingConfigToListingColumnGroupMap(this.state.listingConfigList);
	}

	renderTierSection(listingConfigKey, listingColumnGroupList, shouldDisable = false, index) {
		const {
			deleteEntityFromFlatData,
			flatData,
			listingGroupEntityKey,
			stateCallback,
			updateSettingValue,
		} = this.props;
		const listingConfigMap = this.context?.listingConfigMap || {};

		const {
			listingColumnOptionsMap, listingConfigList,
		} = this.state;
		// Disable tier select and buttons if there are no listing_configs to choose from.
		let isDisabled = false;
		if (!listingConfigList.length) {
			isDisabled = true;
		}

		// Any column group from a specific config can only appear on a single listing group
		Object.entries(flatData).forEach(([ m1Key, m1Val, ]) => {
			if (m1Key.startsWith('listing_group.') && m1Key !== listingGroupEntityKey) {
				Object.keys(m1Val).forEach((m2Key) => {
					if (m2Key.startsWith('listing_column_group.')) {
						if (flatData?.[m2Key]?.[listingConfigKey]) {
							// isDisabled = true;
							console.warn('This is a potentially risky column addition. Any column group from a specific config should only appear on a single listing group.');
						}
					}
				});
			}
		});

		// Any listing group that belongs to a listing collection with more than a single listing group should only have one tier
		const listingGroupComponent = flatData[listingGroupEntityKey]?.parent;
		const listingCollectionComponent = flatData[listingGroupComponent]?.parent;

		let listingGroupCount = 0;
		let otherTierHasColumns = false;
		if (listingColumnGroupList.length === 0) {
			Object.keys(flatData[listingCollectionComponent]).forEach((m1Key) => {
				if (m1Key.startsWith('component.')) {
					Object.entries(flatData[m1Key]).forEach(([ m2Key, m2Val, ]) => {
						if (m2Key === 'description' && m2Val === 'listing_group') {
							listingGroupCount++;

							if (!flatData[m1Key]?.[listingGroupEntityKey]) {
								Object.keys(flatData[listingGroupEntityKey]).forEach((m3Key) => {
									if (m3Key.startsWith('listing_column_group.')) {
										otherTierHasColumns = true;
									}
								});
							}
						}
					});
				}
			});

			if (listingGroupCount > 1 && otherTierHasColumns) {
				// isDisabled = true;
				console.warn('This is a potentially risky column addition. Any listing group that belongs to a listing collection with more than a single listing group should only have one tier.');
			}
		}

		let hasRelation = flatData[listingGroupEntityKey][listingConfigKey];

		const {
			m1ID: listingConfigUUID,
		} = parseEntityName(listingConfigKey);
		const customStyles = {
			control: (provided, state) => {
				return {
					...provided,
					background:  '#fff',
					borderColor: '#9e9e9e',
					minHeight:   '26px',
					height:      'auto',
					fontSize:    '0.8rem',
					boxShadow:   state.isFocused ? null : null,
				};
			},

			valueContainer: (provided) => {
				return {
					...provided,
					height:  '26px',
					padding: '0 6px',
				};
			},

			input: (provided) => {
				return {
					...provided,
					margin: '0px',
				};
			},
			indicatorsContainer: (provided) => {
				return {
					...provided,
					height: '26px',
				};
			},
		};
		let addColumnBtn = (
			<button
				className='button'
				onClick={(e) => {
					e.preventDefault();
					this.addListingColumnGroup(listingConfigUUID);
				}}
			>
				<CustomIcon icon='plus' /> Column
			</button>
		);

		let showSubNavToggle = false;
		if (hasRelation) {
			showSubNavToggle = flatData[listingGroupEntityKey][listingConfigKey].show_sub_nav_button;
		}
		let showSubNavBtn = (
			<button
				className={`${style.mobileDisplay} ${showSubNavToggle ? style.active : ''}`}
				title='Secondary Button'
				aria-label='Secondary Button'
				onClick={(e) => {
					e.preventDefault();
					const newFlatData = {
						...flatData,
					};
					newFlatData[listingGroupEntityKey] = {
						...flatData[listingGroupEntityKey],
					};
					newFlatData[listingGroupEntityKey][listingConfigKey] = {
						...flatData[listingGroupEntityKey][listingConfigKey],
						show_sub_nav_button: !showSubNavToggle,
					};
					stateCallback('flatData', newFlatData);
				}}
			>
				<CustomIcon icon='list' />
			</button>
		);

		let mobileDisplayToggle = false;
		if (hasRelation) {
			mobileDisplayToggle = flatData[listingGroupEntityKey][listingConfigKey].mobile_display;
		}
		let mobileDisplayBtn = (
			<button
				className={`${style.mobileDisplay} ${mobileDisplayToggle ? style.active : ''}`}
				title='Mobile Button'
				aria-label='Mobile Button'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].mobile_display = !mobileDisplayToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='mobile-alt' />
			</button>
		);

		let mobileParentListToggle = false;
		if (hasRelation) {
			mobileParentListToggle = flatData[listingGroupEntityKey][listingConfigKey].mobile_parent_list;
		}
		let mobileParentListBtn = (
			<button
				className={`${style.mobileDisplay} ${mobileParentListToggle ? style.active : ''}`}
				title='Parent Categories'
				aria-label='Parent Categories'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].mobile_parent_list = !mobileParentListToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='tag' />
			</button>
		);

		let mobileCallingToggle = false;
		if (hasRelation) {
			mobileCallingToggle = flatData[listingGroupEntityKey][listingConfigKey].mobile_dialing;
		}
		let mobileCallingBtn = (
			<button
				className={`${style.mobileDisplay} ${mobileCallingToggle ? style.active : ''}`}
				title='Phone Dialing'
				aria-label='Phone Dialing'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].mobile_dialing = !mobileCallingToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='phone' />
			</button>
		);

		let mobileWayfindingToggle = false;
		if (hasRelation) {
			mobileWayfindingToggle = flatData[listingGroupEntityKey][listingConfigKey].mobile_wayfinding;
		}

		let mobileWayfindingBtn = (
			<button
				className={`${style.mobileDisplay} ${mobileWayfindingToggle ? style.active : ''}`}
				title='Wayfinding'
				aria-label='Wayfinding'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].mobile_wayfinding = !mobileWayfindingToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='route' />
			</button>
		);

		let mobilePdfToggle = false;
		if (hasRelation) {
			mobilePdfToggle = flatData[listingGroupEntityKey][listingConfigKey].mobile_pdf;
		}

		let mobilePdfBtn = (
			<button
				className={`${style.mobileDisplay} ${mobilePdfToggle ? style.active : ''}`}
				title='PDF'
				aria-label='PDF'
				data-test-id='mobilePdfButton'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].mobile_pdf = !mobilePdfToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='file-pdf' />
			</button>
		);

		let boxStyleToggle = false;
		if (hasRelation) {
			boxStyleToggle = flatData[listingGroupEntityKey][listingConfigKey].boxstyle_display;
		}
		let boxStyleListingsBtn = (
			<button
				className={`${style.mobileDisplay} ${boxStyleToggle ? style.active : ''}`}
				title='Box Listings'
				aria-label='Box Listings'
				data-test-id='boxListingsButton'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].boxstyle_display = !boxStyleToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='box' />
			</button>
		);

		let showQRCodeToggle = false;
		if (hasRelation) {
			showQRCodeToggle = flatData[listingGroupEntityKey][listingConfigKey].show_qrcode;
		}

		let	showQRCodeBtn = (
			<button
				className={`${style.mobileDisplay} ${showQRCodeToggle ? style.active : ''}`}
				title='Map QR'
				aria-label='Map QR'
				onClick={(e) => {
					e.preventDefault();
					flatData[listingGroupEntityKey][listingConfigKey].show_qrcode = !showQRCodeToggle;
					stateCallback('flatData', flatData);
				}}
			>
				<CustomIcon icon='qrcode' />
			</button>
		);

		let mobileOptions = mobileClickEventOptions;

		let mobileOptionsValue = mobileOptions[0];
		if (hasRelation) {
			mobileOptionsValue = mobileOptions[flatData[listingGroupEntityKey][listingConfigKey].mobile_click_event];
		}

		let touchActionOptions = [
			{
				label: 'None',
				value: touchActionNone,
			},
			{
				label: 'Details Page A',
				value: touchActionDetailsPageA,
			},
			{
				label: 'Details Page B',
				value: touchActionDetailsPageB,
			},
			{
				label: 'Wayfinding A',
				value: touchActionWayfindingA,
			},
			{
				label: 'Wayfinding Modal',
				value: touchActionWayfindingB,
			},
			{
				label: 'Direct WF Modal',
				value: touchActionDirectWayfinding,
			},
			{
				label:    'Tier+1',
				value:    10,
				disabled: shouldDisable,
			},
			{
				label: 'Iframe URL',
				value: touchActionIframe,
			},
			{
				label: 'PDF',
				value: touchActionPDF,
			},
			{
				label: 'Video',
				value: 13,
			},
		];

		let boxLayoutOptions = [
			{
				label: 'Layout 1',
				value: 0,
			},
			{
				label: 'Layout 2',
				value: 1,
			},
		];

		let boxColumnsOptions = [
			{
				label: '1',
				value: 0,
			},
			{
				label: '2',
				value: 1,
			},
			{
				label: '3',
				value: 2,
			},
			{
				label: '4',
				value: 3,
			},
			{
				label: '5',
				value: 4,
			},
		];

		let touchActionOptionsValue = touchActionOptions[0];

		if (hasRelation) {
			const touchActionValue = flatData[listingGroupEntityKey][listingConfigKey].touch_action;
			touchActionOptionsValue = touchActionOptions.find((opt) => {
				return opt.value === touchActionValue;
			}) || touchActionOptions[0];
			if (touchActionOptionsValue.value === 10 && shouldDisable) {
				touchActionOptionsValue = touchActionOptions[0];
			}
		}


		let additionalSearchOptions = this.buildNestedColumnOptions(listingColumnOptionsMap, listingConfigUUID);
		// Filter out options with label 'Video', 'PDF', 'Image', or 'Logo'
		const excludedLabels = [ 'Video', 'PDF', 'Image', 'Logo', ];
		additionalSearchOptions = additionalSearchOptions.map((group) => {
			return {
				...group,
				options: (group.options || []).filter((opt) => {
					return !excludedLabels.includes(opt?.label);
				}),
			};
		});
		let additionalSearchSelected = [];
		if (hasRelation) {
			const csv = flatData[listingGroupEntityKey][listingConfigKey]?.additional_search_fields || '';
			const selectedUUIDs = (csv || '').split(',').filter(Boolean);
			additionalSearchOptions.forEach((group) => {
				(group.options || []).forEach((opt) => {
					if (selectedUUIDs.includes(opt?.value?.uuid)) {
						additionalSearchSelected.push(opt);
					}
				});
			});
		}

		let mobileButtonValue = '';
		if (hasRelation) {
			mobileButtonValue = flatData[listingGroupEntityKey][listingConfigKey].mobile_button_label;
			if (!mobileButtonValue) {
				mobileButtonValue = '';
			}
		}

		let mobileOrderValue = 0;
		if (hasRelation) {
			mobileOrderValue = flatData[listingGroupEntityKey][listingConfigKey].mobile_button_order;
		}
		let boxColumns;
		if (hasRelation) {
			boxColumns = boxColumnsOptions[flatData[listingGroupEntityKey][listingConfigKey].number_of_columns];
		}
		let boxLayout;
		if (hasRelation) {
			boxLayout = boxLayoutOptions[flatData[listingGroupEntityKey][listingConfigKey].active_layout];
		}

		let wayfindingColumnOptions = [
			{
				label: 'None',
				value: '',
			},
		];
		let primaryWFColumn = wayfindingColumnOptions[0];
		let secondaryWFColumn = wayfindingColumnOptions[0];

		let iframeURLOptions = [
			{
				label: 'None',
				value: 0,
			},
		];
		let pdfURLOptions = [
			{
				label: 'None',
				value: 0,
			},
		];
		let videoURLOptions = [
			{
				label: 'None',
				value: 0,
			},
		];
		Object.keys(listingConfigMap[listingConfigUUID]).forEach((childKey) => {
			if (childKey.startsWith('listing_column.')) {
				if (listingConfigMap[listingConfigUUID][childKey]['listing_column_type.string']?.type === 'string') {
					wayfindingColumnOptions.push({
						label: listingConfigMap[listingConfigUUID][childKey].display_name,
						value: childKey.split('.')[1],
					});
				}

				if (childKey.split('.')[1] === flatData[listingGroupEntityKey][listingConfigKey]?.wf_column) {
					primaryWFColumn = {
						label: listingConfigMap[listingConfigUUID][childKey].display_name,
						value: childKey.split('.')[1],
					};
				}

				if (childKey.split('.')[1] === flatData[listingGroupEntityKey][listingConfigKey]?.wf_fallback_column) {
					secondaryWFColumn = {
						label: listingConfigMap[listingConfigUUID][childKey].display_name,
						value: childKey.split('.')[1],
					};
				}

				Object.keys(listingConfigMap[listingConfigUUID][childKey]).forEach((key) => {
					if (listingConfigMap[listingConfigUUID][childKey][key]?.display_name === 'PDF') {
						pdfURLOptions.push({
							label: listingConfigMap[listingConfigUUID][childKey].display_name,
							value: pdfURLOptions.length,
						});
					}
					if (listingConfigMap[listingConfigUUID][childKey][key]?.display_name === 'URL') {
						iframeURLOptions.push({
							label: listingConfigMap[listingConfigUUID][childKey].display_name,
							value: iframeURLOptions.length,
						});
					}
					if (listingConfigMap[listingConfigUUID][childKey][key]?.display_name === 'Video') {
						videoURLOptions.push({
							label: listingConfigMap[listingConfigUUID][childKey].display_name,
							value: videoURLOptions.length,
						});
					}
				});
			}
		});
		let iframeURLValue, pdfURLValue, videoURLValue;
		let iframeURLLabel, pdfURLLabel, videoURLLabel;
		let pdfURLOptionsValue = pdfURLOptions[0];
		let iframeURLOptionsValue = iframeURLOptions[0];
		let videoURLOptionsValue = videoURLOptions[0];

		if (hasRelation) {
			pdfURLValue = flatData[listingGroupEntityKey][listingConfigKey]?.pdf_url_value || '';
			pdfURLLabel = flatData[listingGroupEntityKey][listingConfigKey]?.pdf_url_label || undefined;
			iframeURLValue = flatData[listingGroupEntityKey][listingConfigKey]?.iframe_url_value || '';
			iframeURLLabel = flatData[listingGroupEntityKey][listingConfigKey]?.iframe_url_label || undefined;
			videoURLValue = flatData[listingGroupEntityKey][listingConfigKey]?.video_url_value || '';
			videoURLLabel = flatData[listingGroupEntityKey][listingConfigKey]?.video_url_label || undefined;
			for (let i = 0; i < pdfURLOptions.length; i++) {
				if (pdfURLOptions[i].value === pdfURLValue) {
					pdfURLOptionsValue = {
						'label': pdfURLLabel,
						'value': pdfURLValue,
					};
				}
			}
			for (let i = 0; i < iframeURLOptions.length; i++) {
				if (iframeURLOptions[i].value === iframeURLValue) {
					iframeURLOptionsValue = {
						'label': iframeURLLabel,
						'value': iframeURLValue,
					};
				}
			}
			for (let i = 0; i < videoURLOptions.length; i++) {
				if (videoURLOptions[i].value === videoURLValue) {
					videoURLOptionsValue = {
						'label': videoURLLabel,
						'value': videoURLValue,
					};
				}
			}
		}

		// Enforce limit of 3 column_groups. Remove/modify this when condor can support more columns.
		// commented conditional may be used in the future for a more complex limiter for columns.
		// if (this.state.listingColumnOptionsMap[`listing_config.${listingConfigUUID}`] && listingColumnGroupList.length >= this.state.listingColumnOptionsMap[`listing_config.${listingConfigUUID}`].length || isDisabled) {

		if (listingColumnGroupList.length === 7 || isDisabled) {
			addColumnBtn = null;
		}

		let columnGroups = [];

		// Create listing_column_group elements.
		listingColumnGroupList.forEach((listingColumnGroupKey, i) => {
			if (!flatData[listingGroupEntityKey][listingColumnGroupKey]) {
				return;
			}

			// Set the order for the listing_column_group. Check if the value needs to be updated due to a column_group deletion.
			// Order correction is handled in componentDidUpdate via correctColumnGroupOrders().
			if (flatData[listingGroupEntityKey][listingColumnGroupKey].order !== i + 1) {
				flatData[listingGroupEntityKey][listingColumnGroupKey].order = i + 1;
			}

			let columns = [];

			// Find listing_columns that the listing_column_group owns.
			Object.entries(flatData[listingColumnGroupKey]).forEach(([ listingColumnKey, listingColumnVal, ]) => {
				const {
					m1, m1ID: listingColumnUUID,
				} = parseEntityName(listingColumnKey);

				let columnOrder = 0;
				let nestedOptions = this.buildNestedColumnOptions(listingColumnOptionsMap, listingConfigUUID, listingConfigMap);
				// Build listing column fields if the relation isn't false.
				if (m1 === 'listing_column' && listingColumnVal) {
					let columnSelectValue = null;
					columnOrder++;

					// Search all visible tiers (current + parents) for the selected column.
					for (const group of nestedOptions) {
						for (const option of (group.options || [])) {
							if (option.value.uuid === listingColumnUUID) {
								columnSelectValue = option;
								break;
							}
						}
						if (columnSelectValue) break;
					}

					let checkedHeader;
					let checkedDetailHeaders;
					let labelSeperatorOptions;
					let selectedLabelSeperator;
					if (hasRelation) {
						checkedHeader = flatData[listingColumnGroupKey][listingColumnKey].header_as_label;
					}
					const hasValidTouchAction = (
						flatData[listingGroupEntityKey]?.[listingConfigKey]?.touch_action !== null &&
						flatData[listingGroupEntityKey]?.[listingConfigKey]?.touch_action !== undefined &&
						flatData[listingGroupEntityKey][listingConfigKey].touch_action !== 0
					);
					columns[0] = (
						<React.Fragment key={listingColumnUUID}>
							{listingColumnUUID &&
								<ListingColumnInput
									app={this.props.app}
									accountUUID={this.props.accountUUID}
									attr={'display_name'}
									flatData={flatData}
									label={'Header'}
									listingColumnGroupKey={listingColumnGroupKey}
									stateCallback={stateCallback}
									type={'text'}
									value={flatData[listingColumnGroupKey].display_name}
								/>
							}
							{flatData[listingGroupEntityKey][listingConfigKey]?.boxstyle_display &&
								<div className={style.settingContainer} data-test-id="useLabelInSparkClient">
									<div className={style.settingLabel} data-test-id="settingLabel">Use label in Spark-Client:</div>
									<label className={style.container}>
										<input
											type='checkbox'
											onChange={(e) => {
												flatData[listingColumnGroupKey][listingColumnKey].header_as_label = !flatData[listingColumnGroupKey][listingColumnKey].header_as_label;
												stateCallback('flatData', flatData);
											}}
											checked={checkedHeader}
										/>
										<span className={`${style.checkmark} ${style.boxStyleCheckmark}`}></span>
									</label>
								</div>
							}

							<div className={style.settingContainer} data-test-id="data">
								<div className={style.settingLabel} data-test-id="settingLabel">Data</div>
								<Select
									// Disable the selection of the same data options selected on previous columns
									className={style.buttonsDropdown}
									styles={customStyles}
									options={nestedOptions}
									components={selectComponents}
									dropdownIndicatorTestId="indicator-data"
									optionTestIdPrefix="option"
									onChange={(e) => {
										// Delete the temp listing_column_group_to_listing_column relation if there is one.
										delete flatData[listingColumnGroupKey]['listing_column.'];

										// If there was a previous listing_column_group_to_listing_column, carry the attrs over to the new listing_column.
										// Delete the old relation.
										flatData[listingColumnGroupKey].display_name = flatData[listingColumnGroupKey].display_name || e.label;
										if (columnSelectValue?.value) {
											if (e.value.uuid === columnSelectValue.value.uuid) {
												return;
											}

											flatData[listingColumnGroupKey][`listing_column.${e.value.uuid}`] = flatData[listingColumnGroupKey][`listing_column.${columnSelectValue.value.uuid}`];
											delete flatData[listingColumnGroupKey][`listing_column.${columnSelectValue.value.uuid}`];
										} else {
											// Else set default attributes.
											let defaultValue = {
												order:               columnOrder,
												width:               70,
												sort:                1,
												sort_direction:      0,
												text_align:          0,
												mobile_width:        70,
												mobile_text_align:   0,
												listing_logo:        false,
												mobile_listing_logo: false,
												exclude_from_search: false,
												filter:              '',
												label_seperator:     0,
											};

											if (i === 1) {
												defaultValue = {
													order:               columnOrder,
													width:               30,
													sort:                2,
													sort_direction:      0,
													text_align:          1,
													mobile_width:        30,
													mobile_text_align:   1,
													listing_logo:        false,
													mobile_listing_logo: false,
													exclude_from_search: false,
													filter:              '',
													label_seperator:     0,
												};
											} else if (i > 1) {
												defaultValue = {
													order:               columnOrder,
													width:               0,
													sort:                i + 1,
													sort_direction:      0,
													text_align:          1,
													mobile_width:        0,
													mobile_text_align:   1,
													listing_logo:        false,
													mobile_listing_logo: false,
													exclude_from_search: false,
													filter:              '',
													label_seperator:     0,
												};
											}

											flatData[listingColumnGroupKey][`listing_column.${e.value.uuid}`] = defaultValue;
										}

										stateCallback('flatData', flatData);
									}}
									value={columnSelectValue}
								/>
							</div>

							{/* Only show width, sort, and filter inputs if there is a listingColumnUUID */}
							{listingColumnUUID &&
								<React.Fragment>
									<ListingColumnInput
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										attr={'width'}
										flatData={flatData}
										listingColumnGroupKey={listingColumnGroupKey}
										listingColumnKey={listingColumnKey}
										stateCallback={stateCallback}
										type={'number'}
										value={flatData[listingColumnGroupKey][listingColumnKey].width}
									/>

									<ListingColumnInput
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										attr={'sort'}
										flatData={flatData}
										listingColumnGroupKey={listingColumnGroupKey}
										listingColumnKey={listingColumnKey}
										stateCallback={stateCallback}
										type={'number'}
										value={flatData[listingColumnGroupKey][listingColumnKey].sort}
										labelAccessory={(() => {
											const isDesc = flatData[listingColumnGroupKey][listingColumnKey].sort_direction === 1;

											const setSortDirection = (sortDirection) => {
												flatData[listingColumnGroupKey][listingColumnKey].sort_direction = sortDirection;
												stateCallback('flatData', flatData);
											};

											const sortDirectionName = `${listingColumnGroupKey}-${listingColumnKey}-sort-direction`;

											return (
												<div className={`${style.customSwitch} ${style.sortDirectionSwitch}`} data-test-id="sortDirectionToggle" data-sort-direction={isDesc ? 'DESC' : 'ASC'}>
													<div>
														<input
															type='radio'
															id={`${sortDirectionName}-asc`}
															name={sortDirectionName}
															value='ASC'
															data-test-id="sortDirectionAsc"
															onChange={() => {
																setSortDirection(0);
															}}
															checked={!isDesc}
														/>

														<label
															htmlFor={`${sortDirectionName}-asc`}
															data-test-id="sortDirectionAscLabel"
														>
															<span>ASC</span>
														</label>
													</div>
													<div>
														<input
															type='radio'
															id={`${sortDirectionName}-desc`}
															name={sortDirectionName}
															value='DESC'
															data-test-id="sortDirectionDesc"
															onChange={() => {
																setSortDirection(1);
															}}
															checked={isDesc}
														/>

														<label
															htmlFor={`${sortDirectionName}-desc`}
															data-test-id="sortDirectionDescLabel"
														>
															<span>DESC</span>
														</label>
													</div>
												</div>
											);
										})()}
									/>

									<ListingColumnInput
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										attr={'filter'}
										flatData={flatData}
										listingColumnGroupKey={listingColumnGroupKey}
										listingColumnKey={listingColumnKey}
										stateCallback={stateCallback}
										updateSettingValue={updateSettingValue}
										type={'text'}
										value={flatData[listingColumnGroupKey][listingColumnKey].filter}
									/>

									{(() => {
										// Column types flagged exclude_from_search_default are never searched in Condor.
										// The column can come from a parent tier, so look it up in any config.
										const colObj = [ listingConfigMap[listingConfigUUID], ...Object.values(listingConfigMap), ]
											.map((config) => config?.[`listing_column.${listingColumnUUID}`])
											.find((column) => column && Object.keys(column).some((key) => key.startsWith('listing_column_type.'))) || {};
										const colTypeKey = Object.keys(colObj).find((key) => key.startsWith('listing_column_type.'));
										const excludedByType = Boolean(colTypeKey && colObj[colTypeKey]?.exclude_from_search_default);
										return (
											<ListingColumnCheckbox
												app={this.props.app}
												accountUUID={this.props.accountUUID}
												attr={'exclude_from_search'}
												disabled={excludedByType}
												flatData={flatData}
												hint={excludedByType ? 'Excluded by column type' : undefined}
												label={'Exclude From Search'}
												listingColumnGroupKey={listingColumnGroupKey}
												listingColumnKey={listingColumnKey}
												stateCallback={stateCallback}
												value={flatData[listingColumnGroupKey][listingColumnKey].exclude_from_search == null ? false : flatData[listingColumnGroupKey][listingColumnKey].exclude_from_search}
											/>
										);
									})()}

									<div className={style.settingContainer} data-test-id="textAlign">
										<div className={style.settingLabel} data-test-id="settingLabel">Text Align</div>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={listingGroupDropdownOptions['text_align']}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-textAlign"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingColumnGroupKey][listingColumnKey].text_align = e.value;
												stateCallback('flatData', flatData);
											}}
											value={listingGroupDropdownValues['text_align'][flatData[listingColumnGroupKey][listingColumnKey].text_align]}
										/>
									</div>

									{(() => {
										const colObj = listingConfigMap[listingConfigUUID][`listing_column.${listingColumnUUID}`];
										console.log('colObj', colObj);
										const colType = colObj && colObj['listing_column_type.string'] && colObj['listing_column_type.string'].display_name;
										if (colType === 'Image' || colType === 'Logo') {
											return (
												<ListingColumnCheckbox
													app={this.props.app}
													accountUUID={this.props.accountUUID}
													attr={'listing_logo'}
													flatData={flatData}
													listingColumnGroupKey={listingColumnGroupKey}
													listingColumnKey={listingColumnKey}
													stateCallback={stateCallback}
													value={flatData[listingColumnGroupKey][listingColumnKey].listing_logo == null ? false : flatData[listingColumnGroupKey][listingColumnKey].listing_logo}
												/>
											);
										}
										return null;
									})()}


									<ListingColumnInput
										app={this.props.app}
										accountUUID={this.props.accountUUID}
										attr={'mobile_width'}
										flatData={flatData}
										listingColumnGroupKey={listingColumnGroupKey}
										listingColumnKey={listingColumnKey}
										stateCallback={stateCallback}
										type={'number'}
										value={flatData[listingColumnGroupKey][listingColumnKey].mobile_width}
									/>

									<div className={style.settingContainer} data-test-id="mobileTextAlign">
										<div className={style.settingLabel} data-test-id="settingLabel">Mobile Text Align</div>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={listingGroupDropdownOptions['mobile_text_align']}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-mobileTextAlign"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingColumnGroupKey][listingColumnKey].mobile_text_align = e.value;
												stateCallback('flatData', flatData);
											}}
											value={listingGroupDropdownValues['mobile_text_align'][flatData[listingColumnGroupKey][listingColumnKey].mobile_text_align]}
										/>
									</div>
									{(() => {
										const colObj = listingConfigMap[listingConfigUUID][`listing_column.${listingColumnUUID}`];
										console.log('colObj', colObj);
										const colType = colObj && colObj['listing_column_type.string'] && colObj['listing_column_type.string'].display_name;
										if (colType === 'Image' || colType === 'Logo') {
											return (
												<ListingColumnCheckbox
													app={this.props.app}
													accountUUID={this.props.accountUUID}
													attr={'mobile_listing_logo'}
													flatData={flatData}
													listingColumnGroupKey={listingColumnGroupKey}
													listingColumnKey={listingColumnKey}
													stateCallback={stateCallback}
													value={flatData[listingColumnGroupKey][listingColumnKey].mobile_listing_logo == null ? false : flatData[listingColumnGroupKey][listingColumnKey].mobile_listing_logo}
												/>
											);
										}
										return null;
									})()}

								</React.Fragment>
							}
						</React.Fragment>
					);
				}
			});

			columnGroups.push(
				<div
					className={`${style.listingColumnGroup} ${style.compactListingColumnGroup}`}
					key={listingColumnGroupKey}
					data-test-id={`listingColumnGroup-${listingColumnGroupKey.split('.').pop()}`}
					data-column-id={`t${index + 1}-col${i + 1}`}
				>
					<div className={style.compactListingColumnHeader}>
						Column {i + 1}
					</div>

					{columns}

					<div
						className={style.listingColumnGroupDelete}
						onClick={(e) => {
							e.preventDefault();
							if (window.confirm(`Are you sure you want to delete ${flatData[listingColumnGroupKey].display_name}?`)) {
								// Explicitly remove the listing_group -> listing_column_group relation.
								// deleteEntityFromFlatData relies on `parent` to remove parent relations,
								// but existing listing_column_groups from the API don't have `parent` set.
								flatData[listingGroupEntityKey][listingColumnGroupKey] = false;
								const updatedData = deleteEntityFromFlatData(listingColumnGroupKey, flatData);
								stateCallback('flatData', updatedData);
								this.buildListingConfigToListingColumnGroupMap(this.state.listingConfigList);
								const newListingConfigList = this.state.listingConfigList.map((element) => {
									if (element.uuid === listingConfigKey.split('.')[1] && element.offset >= 1) {
										element.offset -= 1;
									}
									return element;
								});

								this.setState({
									listingConfigList: newListingConfigList,
								});
							}
						}}
					>
						<CustomIcon icon='minus-circle' color='darkred' />
					</div>
				</div>
			);
		});

		const listingConfigDisplayName = listingConfigMap[listingConfigUUID].display_name;
		const listingConfigTier = listingConfigMap[listingConfigUUID].tier;

		let matchingKeys = [];

		if (listingConfigMap[listingConfigUUID].childTiers.length === 0 || shouldDisable) {

			const tierPlusOneOption = touchActionOptions.find((option) => {
				return option.value === 10;
			});
			if (tierPlusOneOption) {
				tierPlusOneOption.isDisabled = true;
			}
		} else {
			Object.entries(listingConfigMap).forEach(([ key, val, ]) => {
				if (listingConfigMap[listingConfigUUID]?.childTiers.includes(key)) {
					matchingKeys.push(key);
				}
			});
		}
		let checkedDetailHeaders;
		let labelSeperatorOptions;
		let selectedLabelSeperator;
		checkedDetailHeaders = flatData[listingGroupEntityKey][listingConfigKey]?.detail_headers;

		const labelSeperatorValue = flatData[listingGroupEntityKey][listingConfigKey]?.label_seperator;

		labelSeperatorOptions = listingGroupDropdownOptions['label_seperator'];

		selectedLabelSeperator = labelSeperatorOptions.find(
			(opt) => {
				return opt.value === labelSeperatorValue;
			}
		);

		let checkedListingPagination;

		checkedListingPagination = flatData[listingGroupEntityKey][listingConfigKey]?.listing_pagination;

		const paginationTimerValue = flatData[listingGroupEntityKey][listingConfigKey]?.pagination_timer;

		const offset = this.state.listingConfigList.find((element) => {
			if (element.uuid === listingConfigKey.split('.')[1]) {
				return true;
			}
			return false;
		}).offset;

		const isExpanded = !!this.state.expandedTiers[listingConfigKey];

		return (
			<div key={listingConfigKey} data-test-id={`t${index + 1}-listingGroup`} className={style.listingGroupTierWrapper}>
				<div
					className={style.listingGroupTierHeader}
					role="button"
					tabIndex={0}
					aria-expanded={isExpanded}
					onClick={(e) => {
						// Don't collapse/expand when the user is selecting header text to copy it.
						const selection = window.getSelection();
						if (selection && !selection.isCollapsed && e.currentTarget.contains(selection.anchorNode)) {
							return;
						}
						return this.toggleTierExpanded(listingConfigKey);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							this.toggleTierExpanded(listingConfigKey);
						}
					}}
					data-test-id={`t${index + 1}-listingGroupTierHeader`}
				>
					<span className={style.listingGroupTierHeaderLabel}>
						<span className={style.listingGroupTierHeaderTier}>Tier {listingConfigTier} Data</span>
						<span className={style.listingGroupTierHeaderName}>{listingConfigDisplayName}</span>
					</span>
					<CustomIcon
						icon="chevron-down"
						className={`${style.listingGroupTierChevron} ${isExpanded ? style.listingGroupTierChevronExpanded : ''}`}
					/>
				</div>
				<div className={`${style.listingGroupTierContent} ${isExpanded ? style.listingGroupTierContentExpanded : ''}`}>
					<div className={style.listingGroupSelectTierContainer}>
						{addColumnBtn}
						{showSubNavBtn}
						{mobileDisplayBtn}
						{mobileParentListBtn}
						{showQRCodeBtn}
						{mobileCallingBtn}
						{mobileWayfindingBtn}
						{mobilePdfBtn}
						{boxStyleListingsBtn}
						<Select
							className={style.buttonsDropdown}
							styles={customStyles}
							options={mobileOptions}
							components={selectComponents}
							dropdownIndicatorTestId="indicator-mobileClickEvent"
							optionTestIdPrefix="option"
							onChange={(e) => {
								flatData[listingGroupEntityKey][listingConfigKey].mobile_click_event = e.value;
								stateCallback('flatData', flatData);
							}}
							value={mobileOptionsValue}
						/>
					</div>
					<div className={style.listingGroupSelectTierContainer}>
						<div className={`${style.settingContainer} ${style.settingContainerShort} ${style.listingGroupSettings}`} data-test-id="settingContainer">
							<label className={style.settingLabel} data-test-id="settingLabel">Mobile / Sub Nav Label</label>
							<input
								type='text'
								onChange={(e) => {
									e.preventDefault();
									flatData[listingGroupEntityKey][listingConfigKey].mobile_button_label = e.target.value;
									console.log('flatData[listingGroupEntityKey][listingConfigKey]', flatData[listingGroupEntityKey][listingConfigKey]);
									stateCallback('flatData', flatData);
								}}
								value={mobileButtonValue}
							/>
						</div>
						<div className={`${style.settingContainer} ${style.settingContainerShort} ${style.listingGroupSettings}`} data-test-id="settingContainer">
							<label className={style.settingLabel} data-test-id="settingLabel">Mobile / Sub Nav Order</label>
							<input
								type='number'
								onChange={(e) => {
									e.preventDefault();
									flatData[listingGroupEntityKey][listingConfigKey].mobile_button_order = parseInt(e.target.value);
									stateCallback('flatData', flatData);
								}}
								value={mobileOrderValue}
							/>
						</div>
						<div className={`${style.settingContainer} ${style.settingContainerShort} ${style.listingGroupSettings}`} data-test-id="settingContainer">
							<label className={style.settingLabel} data-test-id="settingLabel">Touch Action</label>
							<Select
								className={style.buttonsDropdown}
								styles={customStyles}
								options={touchActionOptions}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-touchAction"
								optionTestIdPrefix="option"
								onChange={(e) => {
									flatData[listingGroupEntityKey][listingConfigKey].touch_action = e.value;
									if (e.value === touchActionIframe) {
										flatData[listingGroupEntityKey][listingConfigKey].iframe_url_value =  0;
										flatData[listingGroupEntityKey][listingConfigKey].iframe_url_label = iframeURLOptions[0]?.label;
									}
									if (e.value === touchActionPDF) {
										flatData[listingGroupEntityKey][listingConfigKey].pdf_url_value =  0;
										flatData[listingGroupEntityKey][listingConfigKey].pdf_url_label = pdfURLOptions[0]?.label;
									}
									stateCallback('flatData', flatData);
								}}
								value={touchActionOptionsValue}
							/>
						</div>
						<div className={`${style.settingContainer} ${style.settingContainerShort} ${style.listingGroupSettings}`} data-test-id="settingContainer">
							<label className={style.settingLabel} data-test-id="settingLabel">Secondary Search Fields</label>
							<Select
								isMulti
								closeMenuOnSelect={false}
								hideSelectedOptions={false}
								className={`${style.buttonsDropdown} ${style.limitedWidthSelect}`}
								styles={customStyles}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-secondarySearch"
								optionTestIdPrefix="option"
								// Filter out options whose UUIDs are already assigned to a primary column within this tier.
								options={additionalSearchOptions.map((group) => {
									return {
										...group,
										options: (group.options || []).filter((opt) => {
											let usedUUIDs = [];
											Object.entries(flatData).forEach(([ key, val, ]) => {
												if (key.startsWith('listing_column_group.') && val) {
													Object.entries(val).forEach(([ colKey, colVal, ]) => {
														if (colKey.startsWith('listing_column.') && colVal) {
															const {
																m1ID,
															} = parseEntityName(colKey);
															if (m1ID) {
																usedUUIDs.push(m1ID);
															}
														}
													});
												}
											});
											return !usedUUIDs.includes(opt?.value?.uuid);
										}),
									};
								})}
								onChange={(selected) => {
									const csv = (selected || []).map((opt) => {
										return opt?.value?.uuid;
									}).filter(Boolean).join(',');
									flatData[listingGroupEntityKey][listingConfigKey].additional_search_fields = csv;
									stateCallback('flatData', flatData);
								}}
								value={additionalSearchSelected}
							/>
						</div>

						<div className={style.boxRow}>
							{boxStyleToggle && (
								<>
									<div className={`${style.settingContainer} ${style.listingGroupSettings}`} data-test-id="settingContainer">
										<label className={style.settingLabel} data-test-id="settingLabel">Columns Per Row</label>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={boxColumnsOptions}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-columnsPerRow"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].number_of_columns = e.value;
												stateCallback('flatData', flatData);
											}}
											value={boxColumns}
										/>
									</div>
									<div className={`${style.settingContainer} ${style.listingGroupSettings}`} data-test-id="settingContainer">
										<label className={style.settingLabel} data-test-id="settingLabel">Box Layout Type</label>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={boxLayoutOptions}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-boxLayoutType"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].active_layout = e.value;
												stateCallback('flatData', flatData);
											}}
											value={boxLayout}
										/>
									</div>
								</>
							)}
							{touchActionOptionsValue !== undefined && (touchActionOptionsValue.value === touchActionWayfindingA || touchActionOptionsValue.value === touchActionWayfindingB || touchActionOptionsValue.value === touchActionDirectWayfinding) && (
								<>
									<div className={style.settingContainer} data-test-id="primaryWFColumn">
										<label className={style.settingLabel} data-test-id="settingLabel">Primary WF Column</label>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={wayfindingColumnOptions}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-primaryWFColumn"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].wf_column = e.value;
												stateCallback('flatData', flatData);
											}}
											value={primaryWFColumn}
										/>
									</div>
									<div className={style.settingContainer} data-test-id="secondaryWFColumn">
										<label className={style.settingLabel} data-test-id="settingLabel">Secondary WF Column</label>
										<Select
											className={style.buttonsDropdown}
											styles={customStyles}
											options={wayfindingColumnOptions}
											components={selectComponents}
											dropdownIndicatorTestId="indicator-secondaryWFColumn"
											optionTestIdPrefix="option"
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].wf_fallback_column = e.value;
												stateCallback('flatData', flatData);
											}}
											value={secondaryWFColumn}
										/>
									</div>
								</>
							)}
							{touchActionOptionsValue !== undefined && touchActionOptionsValue.value === touchActionIframe && (
								<div className={style.settingContainer} data-test-id="iframeURL">
									<label className={style.settingLabel} data-test-id="settingLabel">iFrame URL</label>
									<Select
										className={style.buttonsDropdown}
										styles={customStyles}
										options={iframeURLOptions}
										components={selectComponents}
										dropdownIndicatorTestId="indicator-iframeURL"
										optionTestIdPrefix="option"
										onChange={(e) => {
											flatData[listingGroupEntityKey][listingConfigKey].iframe_url_value = e.value;
											flatData[listingGroupEntityKey][listingConfigKey].iframe_url_label = e.label;
											stateCallback('flatData', flatData);
										}}
										value={iframeURLOptionsValue}
									/>
								</div>
							)}
							{((touchActionOptionsValue !== undefined && touchActionOptionsValue.value === touchActionPDF) || mobileOptionsValue?.value === mobileClickDirectPDF || mobilePdfToggle) && (
								<div className={style.settingContainer} data-test-id="pdfURL">
									<label className={style.settingLabel} data-test-id="settingLabel">PDF URL</label>
									<Select
										className={style.buttonsDropdown}
										styles={customStyles}
										options={pdfURLOptions}
										components={selectComponents}
										dropdownIndicatorTestId="indicator-pdfURL"
										optionTestIdPrefix="option"
										onChange={(e) => {
											flatData[listingGroupEntityKey][listingConfigKey].pdf_url_value = e.value;
											flatData[listingGroupEntityKey][listingConfigKey].pdf_url_label = e.label;
											stateCallback('flatData', flatData);
										}}
										value={pdfURLOptionsValue}
									/>
								</div>
							)}
							{touchActionOptionsValue !== undefined && touchActionOptionsValue.value === 13 && (
								<div className={style.settingContainer} data-test-id="videoURL">
									<label className={style.settingLabel} data-test-id="settingLabel">Video URL</label>
									<Select
										className={style.buttonsDropdown}
										styles={customStyles}
										options={videoURLOptions}
										components={selectComponents}
										dropdownIndicatorTestId="indicator-videoURL"
										optionTestIdPrefix="option"
										onChange={(e) => {
											flatData[listingGroupEntityKey][listingConfigKey].video_url_value = e.value;
											flatData[listingGroupEntityKey][listingConfigKey].video_url_label = e.label;
											stateCallback('flatData', flatData);
										}}
										value={videoURLOptionsValue}
									/>
								</div>
							)}
							{touchActionOptionsValue !== undefined && touchActionOptionsValue.value !== 0 && (
								<div className={style.settingContainer} data-test-id="detailsPageHeaders">
									<div className={style.settingLabel} data-test-id="settingLabel">Details Page Headers:</div>
									<label className={style.container}>
										<input
											type='checkbox'
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].detail_headers =
														!flatData[listingGroupEntityKey][listingConfigKey].detail_headers;
												stateCallback('flatData', flatData);
											}}
											checked={checkedDetailHeaders}
										/>
										<span className={`${style.checkmark} ${style.boxStyleCheckmark}`}></span>
									</label>
								</div>
							)}
							{touchActionOptionsValue !== undefined && touchActionOptionsValue.value !== 0 && checkedDetailHeaders && (
								<div className={style.settingContainer} data-test-id="separator">
									<div className={style.settingLabel} data-test-id="settingLabel">Separator</div>
									<Select
										className={style.buttonsDropdown}
										styles={customStyles}
										options={labelSeperatorOptions}
										components={selectComponents}
										dropdownIndicatorTestId="indicator-labelSeparator"
										optionTestIdPrefix="option"
										formatOptionLabel={(option) => {
											if (option.value === 0) return <span>[space]</span>;
											return option.label;
										}}
										onChange={(e) => {
											flatData[listingGroupEntityKey][listingConfigKey].label_seperator = e.value;
											stateCallback('flatData', flatData);
										}}
										value={selectedLabelSeperator}
									/>
								</div>
							)}

							{touchActionOptionsValue !== undefined && (
								<div className={style.settingContainer} data-test-id="listingPagination">
									<div className={style.settingLabel} data-test-id="settingLabel">Listing Pagination:</div>
									<label className={style.container}>
										<input
											type='checkbox'
											onChange={(e) => {
												flatData[listingGroupEntityKey][listingConfigKey].listing_pagination =
														!flatData[listingGroupEntityKey][listingConfigKey].listing_pagination;
												stateCallback('flatData', flatData);
											}}
											checked={checkedListingPagination}
										/>
										<span className={`${style.checkmark} ${style.boxStyleCheckmark}`}></span>
									</label>
								</div>
							)}
							{touchActionOptionsValue !== undefined && checkedListingPagination && (
								<div className={style.settingContainer} data-test-id="paginationTimer">
									<div className={style.settingLabel} data-test-id="settingLabel">Pagination Timer:</div>
									<input
										type='number'
										onChange={(e) => {
											e.preventDefault();
											flatData[listingGroupEntityKey][listingConfigKey].pagination_timer = parseInt(e.target.value);
											stateCallback('flatData', flatData);
										}}
										value={paginationTimerValue}
									/>
								</div>
							)}
						</div>


					</div>

					{(columnGroups.length > 3) ?
						<div className={style.listingColumnGroupContainer} style={{
							display:        'flex',
							justifyContent: 'flex-end',
						}}>
							<div className={style.settingContainer} data-test-id="pageLeft">
								<button
									className={`${style.arrowButton} button ${offset < 1 ? 'button-disabled' : ''}`}
									disabled={offset < 1}
									onClick={(e) => {
										e.preventDefault();

										const newListingConfigList = this.state.listingConfigList.map((element) => {
											if (element.uuid === listingConfigKey.split('.')[1] && element.offset >= 1) {
												element.offset -= 1;
											}
											return element;
										});

										this.setState({
											listingConfigList: newListingConfigList,
										});
									}
									}>
									<CustomIcon icon='arrow-circle-left' />
								</button>
							</div>
							<div className={style.settingContainer} data-test-id="pageRight">
								<button
									className={`${style.arrowButton} button ${offset + 3 >= columnGroups.length ? 'button-disabled' : ''}`}
									disabled={offset + 3 >= columnGroups.length}
									onClick={(e) => {
										e.preventDefault();

										const newListingConfigList = this.state.listingConfigList.map((element) => {
											if (element.uuid === listingConfigKey.split('.')[1] && element.offset + 3 < columnGroups.length) {
												element.offset += 1;
											}
											return element;
										});

										this.setState({
											listingConfigList: newListingConfigList,
										});
									}
									}>
									<CustomIcon icon='arrow-circle-right' />
								</button>
							</div>
						</div> :
						''}
					<div className={`${style.listingColumnGroupContainer} ${style.compactListingColumnGroupContainer}`}>
						{columnGroups.slice(0 + offset, 3 + offset)}
					</div>
				</div>
			</div>
		);
	}


	render() {
		const {
			listingConfigToListingColumnGroupMap,
		} = this.state;

		let innerElm = (
			<div>Please Select A Listing Config in the Listing Collection.</div>
		);

		if (Object.keys(listingConfigToListingColumnGroupMap).length > 0) {
			const listingConfigKeys = Object.keys(listingConfigToListingColumnGroupMap);
			innerElm = listingConfigKeys.map((listingConfigKey, index) => {
				const listingColumnGroupList = listingConfigToListingColumnGroupMap[listingConfigKey];
				const nextListingConfigKey = listingConfigKeys[index + 1];
				const shouldDisable = !nextListingConfigKey || Object.keys(listingConfigToListingColumnGroupMap[nextListingConfigKey]).length === 0;
				return this.renderTierSection(listingConfigKey, listingColumnGroupList, shouldDisable, index);
			});
		}
		return (
			<div className={style.listingGroupDataContainer}>
				<div className={style.listingGroupSettingSectionHeader}>DATA</div>
				{innerElm}
			</div>
		);
	}
}

export default TemplateSkeleton;
export {
	ListingGroupCustomField,
};
